// Psychology Assessment Platform - v2.6.1 (Feb 2026)
// Last updated: 2026-02-03 - Added PDF download for draft assessments
import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, CheckCircle2, FileText, Settings, Save, RotateCcw, Phone, Mail, Printer, MessageSquare, Linkedin, Facebook, Instagram, Download } from "lucide-react";
import { toast } from "sonner";
import AdminDashboard from "./AdminDashboard";
import ASDObservationForm from "./components/ASDObservationForm";
import UniversalObservationForm from "./components/UniversalObservationForm";
import DASI2SynthesisForm from "./components/DASI2SynthesisForm";
import SLDAcademicForm from "./components/SLDAcademicForm";
import NeuroDevPreForm from "./components/NeuroDevPreForm";
import DASIInformantForm from "./components/DASIInformantForm";
import UnderstandingLearningForm from "./components/UnderstandingLearningForm";
import NeuroDevPreAdultForm from "./components/NeuroDevPreAdultForm";
import ACESRAdultsForm from "./components/ACESRAdultsForm";
import ACEplusInformantForm from "./components/ACEplusInformantForm";
import ACEInformantForm from "./components/ACEInformantForm";
import FileUploadForm from "./components/FileUploadForm";
import IntakeConsentForm from "./components/IntakeConsentForm";
import StartPage from "./components/StartPage";
import { readUrlClientCode } from "./components/ClientCodeField";
import IntakeLanding, { INTAKE_SLUGS } from "./components/IntakeLanding";
import ResultsPage from "./components/ResultsPage";
import LeaveFormDialog from "./components/LeaveFormDialog";
import { draftIdentity, saveDeviceDraft, loadDeviceDraft, clearDeviceDraft, sweepDeviceDrafts, firstNameOf, startedOnText } from "./lib/deviceDrafts";

import { API, SENDING_ON, isPreviewBlocked, PREVIEW_MESSAGE as PREVIEW_TOAST } from "./lib/portalMode";
import PreviewNote from "./components/PreviewNote";
import DayFirstDate from "./components/DayFirstDate";
import ADHDScreenerForm from "./components/ADHDScreenerForm";
import { scaleOf, upgradeSavedAnswers, CHECK_MESSAGE } from "./lib/responseScale";
import { isBirthDate, parseDayFirst, toDayFirst, assessmentDateProblem } from "./lib/dayFirst";

function App() {
  const [page, setPage] = useState("start"); // start, assessment, results, admin
  const [name, setName] = useState(""); // Person being assessed
  const [completedByName, setCompletedByName] = useState(""); // Person completing the form
  const [clientCode, setClientCode] = useState(() => readUrlClientCode()); // Person-being-assessed code (2 letters + MMYY)
  const [noCode, setNoCode] = useState(false); // user chose "I don't know their date of birth"
  const [codePrefilled, setCodePrefilled] = useState(() => !!readUrlClientCode()); // code came pre-filled from the link
  const [codeRequired, setCodeRequired] = useState(false); // CODE_REQUIRED runtime flag
  const [showNamesPage, setShowNamesPage] = useState(false); // names step before question 1
  // Format date as DD/MM/YYYY for Australian format
  const formatDateAU = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  const [date, setDate] = useState(formatDateAU(new Date()));
  const [selectedAssessment, setSelectedAssessment] = useState("");
  const [assessmentConfig, setAssessmentConfig] = useState(null);
  const [checkAnswerIds, setCheckAnswerIds] = useState([]); // AQ-10 answers converted from the old scale, to check
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  // Read URL assessment parameter synchronously so the gateway vs form decision is instant
  const initialParams = new URLSearchParams(window.location.search);
  const initialAssessmentParam = initialParams.get('assessment');
  const [preselectedAssessment, setPreselectedAssessment] = useState(initialAssessmentParam || null);
  const [agreementToken] = useState(initialParams.get('agreement') || null);
  const [preselectedLabel, setPreselectedLabel] = useState(null);
  const [clientReference, setClientReference] = useState("");
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(true);
  const [incompleteItems, setIncompleteItems] = useState([]);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [draftId, setDraftId] = useState(null);  // Server-side draft ID
  const [savingToServer, setSavingToServer] = useState(false);

  // Device copy of the answers (lib/deviceDrafts.js): filed under form + ref/agreement token + client code, so a
  // link for one client never sees another client's answers. Set when a form starts.
  const [deviceDraft, setDeviceDraft] = useState(null); // { key, linkKeyed }
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Expired device copies (7 days since last save) are cleared when the portal opens.
  useEffect(() => { sweepDeviceDrafts(); }, []);

  const identityFor = useCallback((assessmentType, typedCode) => {
    const urlCode = readUrlClientCode();
    let urlRef = "";
    try {
      const p = new URLSearchParams(window.location.search);
      urlRef = p.get('client') || p.get('ref') || "";
    } catch (e) { /* no URL */ }
    const ref = clientReference || urlRef;
    return draftIdentity({
      form: assessmentType,
      ref,
      token: agreementToken,
      code: typedCode || urlCode,
      linkKeyed: !!(ref || agreementToken || urlCode),
    });
  }, [clientReference, agreementToken]);

  // Save to this device (immediate, for browser recovery)
  const saveProgressLocal = useCallback((assessmentType, data) => {
    if (!deviceDraft) return;
    saveDeviceDraft(deviceDraft.key, data);
  }, [deviceDraft]);

  // Save to server (for admin visibility)
  const saveProgressToServer = useCallback(async (assessmentType, data, currentDraftId) => {
    // Off the live portal nothing is saved to the server (see lib/portalMode.js).
    if (!SENDING_ON) return currentDraftId;
    // Only save if we have at least one answer
    if (Object.keys(data.responses || {}).length === 0) return currentDraftId;
    
    try {
      setSavingToServer(true);
      const response = await axios.post(`${API}/assessments/draft`, {
        name: data.name || '',
        completed_by: data.completedByName || '',
        client_code: data.clientCode || '',
        date: data.date,
        assessment_type: assessmentType,
        responses: data.responses,
        ref: data.ref || null,
        response_scale: data.responseScale || null,
        draft_id: currentDraftId
      });
      
      setLastSaved(new Date());
      
      // Show a subtle toast on first save
      if (!currentDraftId) {
        toast.success("Progress saved to server", { duration: 2000 });
      }
      
      // Return the draft ID for future updates
      return response.data.id;
    } catch (error) {
      console.error('Failed to save draft to server:', error);
      return currentDraftId;
    } finally {
      setSavingToServer(false);
    }
  }, []);

  const loadSavedProgress = useCallback((assessmentType, key) => {
    const parsed = loadDeviceDraft(key);
    if (!parsed) return null;
    // DASS-Y was replaced with the official 2022 form (item meanings changed).
    // Discard any pre-replacement local draft so old answers can't map onto new items.
    if (assessmentType === 'DASS-Y' && parsed.formVersion !== 'DASS-Y 2022 official') {
      clearDeviceDraft(key);
      return null;
    }
    return parsed;
  }, []);

  // Clears this form's device copy for this person only. Never touches the server copy.
  const clearSavedProgress = useCallback(() => {
    if (deviceDraft) clearDeviceDraft(deviceDraft.key);
    setLastSaved(null);
    setDraftId(null);
  }, [deviceDraft]);

  // Auto-save to localStorage whenever responses change (debounced 500ms)
  useEffect(() => {
    if (page === 'assessment' && selectedAssessment && Object.keys(responses).length > 0) {
      const timeoutId = setTimeout(() => {
        saveProgressLocal(selectedAssessment, {
          responses,
          name,
          completedByName,
          clientCode,
          date,
          ref: clientReference,
          formVersion: selectedAssessment === 'DASS-Y' ? 'DASS-Y 2022 official' : undefined,
          responseScale: scaleOf(assessmentConfig) || undefined,
          draftId
        });
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [responses, name, completedByName, date, page, selectedAssessment, draftId, clientReference, saveProgressLocal, assessmentConfig]);

  // Auto-save to server every 30 seconds (less frequent to reduce server load)
  useEffect(() => {
    if (page === 'assessment' && selectedAssessment && Object.keys(responses).length > 0) {
      const intervalId = setInterval(async () => {
        const newDraftId = await saveProgressToServer(selectedAssessment, {
          responses,
          name,
          completedByName,
          clientCode,
          date,
          ref: clientReference,
          responseScale: scaleOf(assessmentConfig) || undefined
        }, draftId);
        if (newDraftId && newDraftId !== draftId) {
          setDraftId(newDraftId);
        }
      }, 30000); // Save to server every 30 seconds
      
      return () => clearInterval(intervalId);
    }
  }, [page, selectedAssessment, responses, name, completedByName, date, draftId, clientReference, saveProgressToServer, assessmentConfig]);

  // Also save to server when user answers a question (debounced 5 seconds)
  useEffect(() => {
    if (page === 'assessment' && selectedAssessment && Object.keys(responses).length > 0) {
      const timeoutId = setTimeout(async () => {
        const newDraftId = await saveProgressToServer(selectedAssessment, {
          responses,
          name,
          completedByName,
          clientCode,
          date,
          ref: clientReference,
          responseScale: scaleOf(assessmentConfig) || undefined
        }, draftId);
        if (newDraftId && newDraftId !== draftId) {
          setDraftId(newDraftId);
        }
      }, 5000); // Save to server 5 seconds after last change
      
      return () => clearTimeout(timeoutId);
    }
  }, [responses, page, selectedAssessment, name, completedByName, date, draftId, clientReference, saveProgressToServer, assessmentConfig]);

  // Load available assessments from API
  useEffect(() => {
    axios.get(`${API}/forms/runtime-config`)
      .then((r) => { setCodeRequired(!!r.data?.code_required); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadAssessments = async () => {
      try {
        setLoadingAssessments(true);
        const response = await axios.get(`${API}/assessments/config`);
        const assessmentData = response.data;
        
        // Convert API response to assessment types format, filtering out hidden assessments for dropdown
        const types = Object.keys(assessmentData)
          .filter(key => !assessmentData[key].hidden) // Exclude hidden assessments from dropdown
          .map(key => ({
            value: key,
            label: assessmentData[key].name || `${key} - ${assessmentData[key].description || 'Assessment'}`
          }));
        
        setAssessmentTypes(types);
        
        // Check for URL parameters after assessments are loaded
        // Note: Hidden assessments can still be accessed via direct URL
        const params = new URLSearchParams(window.location.search);
        const assessmentParam = params.get('assessment');
        const clientParam = params.get('client') || params.get('ref');
        
        if (assessmentParam) {
          // Validate assessment exists (including hidden ones for direct URL access)
          const allTypes = Object.keys(assessmentData).map(key => ({
            value: key,
            label: assessmentData[key].name || key
          }));
          const validAssessment = allTypes.find(
            type => type.value.toLowerCase() === assessmentParam.toLowerCase()
          );
          
          if (validAssessment) {
            setSelectedAssessment(validAssessment.value);
            setPreselectedAssessment(validAssessment.value);
            setPreselectedLabel(validAssessment.label);
          }
        }
        
        if (clientParam) {
          setClientReference(clientParam);
        }
        
      } catch (error) {
        console.error('Failed to load assessments:', error);
        if (!isPreviewBlocked(error)) toast.error("Failed to load available assessments");
      } finally {
        setLoadingAssessments(false);
      }
    };
    
    loadAssessments();
  }, []);

  const startIntake = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/assessments/config`);
      setAssessmentConfig(response.data[preselectedAssessment]);
      setSelectedAssessment(preselectedAssessment);
      setResponses({});
      // Intake, consent and service agreement forms now offer "Continue where I left off" too.
      const identity = identityFor(preselectedAssessment, "");
      setDeviceDraft(identity);
      const saved = loadSavedProgress(preselectedAssessment, identity.key);
      if (saved && Object.keys(saved.responses || {}).length > 0) {
        setSavedProgress(saved);
        setShowRestorePrompt(true);
        return;
      }
      setPage("assessment");
    } catch (error) {
      if (isPreviewBlocked(error)) toast.message(PREVIEW_TOAST); else toast.error("Failed to load the form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const startAssessment = async () => {
    if (!selectedAssessment) {
      toast.error("Please select an assessment");
      return;
    }

    // The assessment date goes into the PDF file name: a real day/month/year date, not in the future.
    const dateProblem = assessmentDateProblem(date);
    if (dateProblem) {
      toast.error(dateProblem);
      return;
    }
    const tidyDate = toDayFirst(parseDayFirst(date));
    if (tidyDate !== date) setDate(tidyDate);

    // Check if this is a qualitative assessment that allows partial completion
    const qualitativeNoValidation = [
      'TOD-C-Parent', 'TOD-C-Self', 'TOD-C-Teacher', 'TOD-E-Parent', 'TOD-E-Teacher',
      'Parent-Check-In', 'Teacher-Check-In', 'Adult-Check-In', 'Child-Check-In',
      'My-Journal', 'Assessment-Checklist',
      'ADOS2-Toddler', 'ADOS2-Module1', 'ADOS2-Module2', 'ADOS2-Module3', 'ADOS2-Module4',
      'ACE-Clinician', 'DASI-2-Clinician',
      'ACE-Informant-Teacher', 'ACEplus-Informant-Childhood', 'ACEplus-Informant-Adult', 'ACE-Interview', 'CAST', 'CATI', 'WURS-25',
      'Teacher-Dev', 'ACE-SR-12', 'ACE-SR-Adults', 'ACE-Informant', 'ACE-Informant-Inattention',
      'DASI-Background', 'DASI-IR', 'DASI-SR', 'Neuro-Dev-Pre', 'Neuro-Dev-Pre-Adult', 'Neuro-Dev-Pre-Parent', 'Neuro-Dev-Pre-v2', 'FASD-Neuro-History', 'Clinical-Observation-Capture', 'ADOS-DASI-Observation-Capture', 
      'ACE-Parent', 'MIGDAS-Teacher',
      'Parent-Weekly-Mood', 'Teacher-Weekly-Mood', 'Parent-Weekly-Event', 'Brain-Discovery-Parent', 'Brain-Discovery-Self',
      'Parent-Child-Sleep-Diary',
      'Autism-Clinician-Checklist', 'ADHD-Clinician-Checklist',
      'Autism-Clinician-Checklist-Structured', 'ADHD-Clinician-Checklist-Structured',
      'DASI2-Observational-Synthesis',
      'ACEv2-ADHD-Synthesis',
      'Understanding-Your-Learning',
      'SLD-Observation-Checklist',
      'ASD-Observation-Capture-v2',
      'Universal-Observation-Capture-v2',
      'DASI2-Synthesis-v2',
      'SLD-Academic-Observation-v2',
      'Parent-Observation-Notes',
      'File-Upload',
      'ADHD-Screener'
    ];
    const isQualitativeNoValidation = qualitativeNoValidation.includes(selectedAssessment);

    try {
      setLoading(true);
      const response = await axios.get(`${API}/assessments/config`);
      const cfg = response.data[selectedAssessment];
      setAssessmentConfig(cfg);
      const isIntake = !!(cfg && cfg.is_intake_form);

      // Check for saved progress on this device, for this person only
      const identity = identityFor(selectedAssessment, noCode ? "" : clientCode);
      setDeviceDraft(identity);
      const saved = loadSavedProgress(selectedAssessment, identity.key);
      if (saved && Object.keys(saved.responses || {}).length > 0) {
        setSavedProgress(saved);
        setShowRestorePrompt(true);
      } else {
        // Fresh start: non-intake questionnaires begin on the names page
        setShowNamesPage(!isIntake);
        setPage("assessment");
        if (isQualitativeNoValidation) {
          toast.success("You don't need to answer every question. Just share what feels relevant.", { duration: 5000 });
        } else {
          toast.success("Assessment loaded successfully");
        }
      }
    } catch (error) {
      if (isPreviewBlocked(error)) toast.message(PREVIEW_TOAST); else toast.error("Failed to load assessment. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const restoreProgress = () => {
    // Check if this is a qualitative assessment that allows partial completion
    const qualitativeNoValidation = [
      'TOD-C-Parent', 'TOD-C-Self', 'TOD-C-Teacher', 'TOD-E-Parent', 'TOD-E-Teacher',
      'Parent-Check-In', 'Teacher-Check-In', 'Adult-Check-In', 'Child-Check-In',
      'My-Journal', 'Assessment-Checklist',
      'ADOS2-Toddler', 'ADOS2-Module1', 'ADOS2-Module2', 'ADOS2-Module3', 'ADOS2-Module4',
      'ACE-Clinician', 'DASI-2-Clinician',
      'ACE-Informant-Teacher', 'ACEplus-Informant-Childhood', 'ACEplus-Informant-Adult', 'ACE-Interview', 'CAST', 'CATI', 'WURS-25',
      'Teacher-Dev', 'ACE-SR-12', 'ACE-SR-Adults', 'ACE-Informant', 'ACE-Informant-Inattention',
      'DASI-Background', 'DASI-IR', 'DASI-SR', 'Neuro-Dev-Pre', 'Neuro-Dev-Pre-Adult', 'Neuro-Dev-Pre-Parent', 'Neuro-Dev-Pre-v2', 'FASD-Neuro-History', 'Clinical-Observation-Capture', 'ADOS-DASI-Observation-Capture', 
      'ACE-Parent', 'MIGDAS-Teacher',
      'Parent-Weekly-Mood', 'Teacher-Weekly-Mood', 'Parent-Weekly-Event', 'Brain-Discovery-Parent', 'Brain-Discovery-Self',
      'Parent-Child-Sleep-Diary',
      'Autism-Clinician-Checklist', 'ADHD-Clinician-Checklist',
      'Autism-Clinician-Checklist-Structured', 'ADHD-Clinician-Checklist-Structured',
      'DASI2-Observational-Synthesis',
      'ACEv2-ADHD-Synthesis',
      'Understanding-Your-Learning',
      'SLD-Observation-Checklist',
      'ASD-Observation-Capture-v2',
      'Universal-Observation-Capture-v2',
      'DASI2-Synthesis-v2',
      'SLD-Academic-Observation-v2',
      'Parent-Observation-Notes',
      'File-Upload',
      'ADHD-Screener'
    ];
    const isQualitativeNoValidation = qualitativeNoValidation.includes(selectedAssessment);

    if (savedProgress) {
      const upgraded = upgradeSavedAnswers(assessmentConfig, savedProgress);
      setResponses(upgraded.responses);
      setCheckAnswerIds(upgraded.checkIds);
      if (upgraded.checkIds.length) toast.message(CHECK_MESSAGE, { duration: 9000 });
      if (savedProgress.name) setName(savedProgress.name);
      if (savedProgress.completedByName) setCompletedByName(savedProgress.completedByName);
      if (savedProgress.date) setDate(savedProgress.date);
      if (savedProgress.draftId) setDraftId(savedProgress.draftId);
      if (savedProgress.ref) setClientReference(savedProgress.ref);
      if (savedProgress.clientCode) setClientCode(savedProgress.clientCode);
      
      const answeredCount = Object.keys(savedProgress.responses || {}).length;
      const savedTime = savedProgress.savedAt ? new Date(savedProgress.savedAt).toLocaleString() : 'unknown';
      toast.success(`Restored ${answeredCount} answers (saved ${savedTime})`);
    }
    setShowRestorePrompt(false);
    setSavedProgress(null);
    // Resume: if the draft already has both names, skip the names page; otherwise show it
    setShowNamesPage(
      !(assessmentConfig && assessmentConfig.is_intake_form) &&
      !(savedProgress && savedProgress.name && savedProgress.completedByName)
    );
    setPage("assessment");
    
    // Show reminder for qualitative assessments
    if (isQualitativeNoValidation) {
      setTimeout(() => {
        toast("You don't need to answer every question. Just share what feels relevant.", { duration: 5000, icon: 'ℹ️' });
      }, 1500);
    }
  };

  const startFresh = async () => {
    // Check if this is a qualitative assessment that allows partial completion
    const qualitativeNoValidation = [
      'TOD-C-Parent', 'TOD-C-Self', 'TOD-C-Teacher', 'TOD-E-Parent', 'TOD-E-Teacher',
      'Parent-Check-In', 'Teacher-Check-In', 'Adult-Check-In', 'Child-Check-In',
      'My-Journal', 'Assessment-Checklist',
      'ADOS2-Toddler', 'ADOS2-Module1', 'ADOS2-Module2', 'ADOS2-Module3', 'ADOS2-Module4',
      'ACE-Clinician', 'DASI-2-Clinician',
      'ACE-Informant-Teacher', 'ACEplus-Informant-Childhood', 'ACEplus-Informant-Adult', 'ACE-Interview', 'CAST', 'CATI', 'WURS-25',
      'Teacher-Dev', 'ACE-SR-12', 'ACE-SR-Adults', 'ACE-Informant', 'ACE-Informant-Inattention',
      'DASI-Background', 'DASI-IR', 'DASI-SR', 'Neuro-Dev-Pre', 'Neuro-Dev-Pre-Adult', 'Neuro-Dev-Pre-Parent', 'Neuro-Dev-Pre-v2', 'FASD-Neuro-History', 'Clinical-Observation-Capture', 'ADOS-DASI-Observation-Capture', 
      'ACE-Parent', 'MIGDAS-Teacher',
      'Parent-Weekly-Mood', 'Teacher-Weekly-Mood', 'Parent-Weekly-Event', 'Brain-Discovery-Parent', 'Brain-Discovery-Self',
      'Parent-Child-Sleep-Diary',
      'Autism-Clinician-Checklist', 'ADHD-Clinician-Checklist',
      'Autism-Clinician-Checklist-Structured', 'ADHD-Clinician-Checklist-Structured',
      'DASI2-Observational-Synthesis',
      'ACEv2-ADHD-Synthesis',
      'Understanding-Your-Learning',
      'SLD-Observation-Checklist',
      'ASD-Observation-Capture-v2',
      'Universal-Observation-Capture-v2',
      'DASI2-Synthesis-v2',
      'SLD-Academic-Observation-v2',
      'Parent-Observation-Notes',
      'File-Upload',
      'ADHD-Screener'
    ];
    const isQualitativeNoValidation = qualitativeNoValidation.includes(selectedAssessment);

    // Draft cleanup is handled server-side by SubmitAssessment
    clearSavedProgress(selectedAssessment);
    setResponses({});
    setDraftId(null);
    setShowRestorePrompt(false);
    setSavedProgress(null);
    setShowNamesPage(!(assessmentConfig && assessmentConfig.is_intake_form));
    setPage("assessment");
    
    if (isQualitativeNoValidation) {
      toast.success("You don't need to answer every question. Just share what feels relevant.", { duration: 5000 });
    } else {
      toast.success("Starting fresh assessment");
    }
  };

  const submitAssessment = async () => {
    if (!SENDING_ON) { toast.message(PREVIEW_TOAST); return; }
    // Skip validation for qualitative assessments - they allow partial completion
    // Exception: Client-Intake still requires all fields
    const qualitativeNoValidation = [
      'TOD-C-Parent', 'TOD-C-Self', 'TOD-C-Teacher', 'TOD-E-Parent', 'TOD-E-Teacher',
      'Parent-Check-In', 'Teacher-Check-In', 'Adult-Check-In', 'Child-Check-In',
      'My-Journal', 'Assessment-Checklist',
      'ADOS2-Toddler', 'ADOS2-Module1', 'ADOS2-Module2', 'ADOS2-Module3', 'ADOS2-Module4',
      'ACE-Clinician', 'DASI-2-Clinician',
      'Record-of-Events', 'Things-Im-Proud-Of', 'Therapy-Goals',
      'ACE-Informant-Teacher', 'ACEplus-Informant-Childhood', 'ACEplus-Informant-Adult', 'ACE-Interview', 'CAST', 'CATI', 'WURS-25',
      'Teacher-Dev', 'ACE-SR-12', 'ACE-SR-Adults', 'ACE-Informant', 'ACE-Informant-Inattention',
      'DASI-Background', 'DASI-IR', 'DASI-SR', 'Neuro-Dev-Pre', 'Neuro-Dev-Pre-Adult', 'Neuro-Dev-Pre-Parent', 'Neuro-Dev-Pre-v2', 'FASD-Neuro-History', 'Clinical-Observation-Capture', 'ADOS-DASI-Observation-Capture', 
      'ACE-Parent', 'MIGDAS-Teacher',
      'Parent-Weekly-Mood', 'Teacher-Weekly-Mood', 'Parent-Weekly-Event', 'Brain-Discovery-Parent', 'Brain-Discovery-Self',
      'Parent-Child-Sleep-Diary',
      'Autism-Clinician-Checklist', 'ADHD-Clinician-Checklist',
      'Autism-Clinician-Checklist-Structured', 'ADHD-Clinician-Checklist-Structured',
      'DASI2-Observational-Synthesis',
      'ACEv2-ADHD-Synthesis',
      'Understanding-Your-Learning',
      'SLD-Observation-Checklist',
      'ASD-Observation-Capture-v2',
      'Universal-Observation-Capture-v2',
      'DASI2-Synthesis-v2',
      'SLD-Academic-Observation-v2',
      'Parent-Observation-Notes',
      'File-Upload',
      'ADHD-Screener'
    ];
    const skipValidation = qualitativeNoValidation.includes(selectedAssessment);
    
    // Find unanswered questions (skip calculated_age fields as they are auto-filled)
    if (assessmentConfig && !skipValidation) {
      const unanswered = assessmentConfig.items.filter(item => {
        // Skip calculated_age fields - they are auto-filled
        if (item.input_type === 'calculated_age') return false;
        
        const response = responses[item.id];
        return response === undefined || response === null || response === '';
      }).map(item => item.id);
      
      if (unanswered.length > 0) {
        setIncompleteItems(unanswered);
        toast.error(`Please complete ${unanswered.length} unanswered question${unanswered.length > 1 ? 's' : ''} (highlighted in red)`);
        
        // Scroll to first incomplete item after state updates
        setTimeout(() => {
          const firstIncomplete = document.querySelector(`[data-testid="question-${unanswered[0]}"]`);
          if (firstIncomplete) {
            firstIncomplete.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }
    }
    
    // Clear any previous incomplete highlights
    setIncompleteItems([]);

    // For File-Upload, the upload is handled separately
    if (selectedAssessment === 'File-Upload') {
      if (responses.uploaded_files && responses.uploaded_files.length > 0) {
        // Files already uploaded via the upload endpoint
        setResults({
          name,
          date,
          assessment_type: 'File-Upload',
          scores: { file_count: responses.uploaded_files.length, is_qualitative: true },
          interpretation: { file_count: responses.uploaded_files.length, is_qualitative: true },
          clinical_interpretation: `File upload completed for ${responses.client_name || name}. ${responses.uploaded_files.length} file(s) uploaded and converted to PDF.`,
        });
        clearSavedProgress(selectedAssessment);
        setDraftId(null);
        setPage("results");
        toast.success("File upload completed successfully!");
        return;
      } else {
        toast.error("Please upload at least one file before submitting");
        return;
      }
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/assessments/submit`, {
        name,
        completed_by: completedByName,
        date,
        assessment_type: selectedAssessment,
        responses,
        ref: clientReference || null,
        client_code: clientCode || null,
        response_scale: scaleOf(assessmentConfig)
      });
      setResults(response.data);
      // Clear saved progress after successful submission (both local and server draft)
      clearSavedProgress(selectedAssessment);
      setDraftId(null);
      setPage("results");
      toast.success("Assessment completed successfully!");
    } catch (error) {
      toast.error("Failed to submit assessment");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Leaving a form part-way. keepAnswers=true saves the device copy now and leaves it in place;
  // keepAnswers=false clears this form's device copy only. The server copy is never touched here.
  const leaveForm = (keepAnswers) => {
    setShowLeaveConfirm(false);
    setCheckAnswerIds([]);
    if (keepAnswers) {
      if (selectedAssessment && Object.keys(responses).length > 0) {
        saveProgressLocal(selectedAssessment, {
          responses, name, completedByName, clientCode, date, ref: clientReference,
          formVersion: selectedAssessment === 'DASS-Y' ? 'DASS-Y 2022 official' : undefined,
          responseScale: scaleOf(assessmentConfig) || undefined,
          draftId
        });
      }
    } else {
      clearSavedProgress();
    }
    setDeviceDraft(null);
    setPage("start");
    setName("");
    setCompletedByName("");
    setDate(formatDateAU(new Date()));
    setSelectedAssessment("");
    setAssessmentConfig(null);
    setResponses({});
    setResults(null);
    setLastSaved(null);
    setDraftId(null);
    window.scrollTo(0, 0);
  };

  const restartSameAssessment = () => {
    // Keep the same assessment type, just clear the form for a new submission
    const currentAssessment = selectedAssessment;
    const currentConfig = assessmentConfig;
    
    if (currentAssessment) {
      clearSavedProgress(currentAssessment);
    }
    
    // Reset form but keep assessment selected
    setPage("start");
    setName("");
    setCompletedByName("");
    setDate(formatDateAU(new Date()));
    setResponses({});
    setResults(null);
    setLastSaved(null);
    setDraftId(null);
    
    // Keep the assessment selected
    setSelectedAssessment(currentAssessment);
    setAssessmentConfig(currentConfig);
  };

  // Check for admin route
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('admin') === 'true') {
      setPage('admin');
    }
  }, []);

  // Footer component
  const Footer = () => (
    <footer className="site-footer">
      <div className="footer-content">
        <div className="footer-main">
          <div className="footer-brand">
            <a href="https://www.clinicalminds.net" target="_blank" rel="noopener noreferrer">
              <img 
                src="/brand/logo-full.png" 
                alt="Clinical Minds Psychology"
                className="footer-logo"
              />
            </a>
            <p className="footer-tagline">Collaborative, Compassionate and Practical Psychology Services, Australia Wide</p>
          </div>
          <div className="footer-contact">
            <h4 className="footer-heading">Contact</h4>
            <div className="contact-item">
              <Phone size={16} />
              <span>02 6600 2710 (phone)</span>
            </div>
            <div className="contact-item">
              <MessageSquare size={16} />
              <span>0415 220 944 (SMS)</span>
            </div>
            <div className="contact-item">
              <Printer size={16} />
              <span>02 6699 1457 (Fax)</span>
            </div>
            <div className="contact-item">
              <Mail size={16} />
              <a href="mailto:contact@clinicalminds.net">contact@clinicalminds.net</a>
            </div>
          </div>
        </div>
        <div className="footer-divider"></div>
        <div className="footer-bottom">
          <p className="footer-copyright">© 2025 Clinical Minds Psychology. All rights reserved.</p>
          <div className="footer-social">
            <a href="https://www.linkedin.com/company/clinical-minds-psychology" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <Linkedin size={20} />
            </a>
            <a href="https://www.facebook.com/clinicalmindspsychology" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <Facebook size={20} />
            </a>
            <a href="https://www.instagram.com/clinicalmindspsychology" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <Instagram size={20} />
            </a>
          </div>
          <div className="footer-links">
            <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            <span className="footer-link-divider">•</span>
            <a href="https://www.clinicalminds.net/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
          </div>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="app-container">
      <PreviewNote />
      {/* Resume prompt. When the link itself didn't name the person (no ref, agreement token or code), the family
          only sees the start date and the first name entered before choosing; no answers are shown. */}
      {showRestorePrompt && (
        <div className="restore-prompt-overlay" data-testid="restore-prompt">
          <Card className="restore-prompt-card">
            {deviceDraft && !deviceDraft.linkKeyed ? (
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Save className="h-5 w-5 text-blue-500" />
                  Answers already started
                </CardTitle>
                <CardDescription data-testid="restore-confirm-text">
                  Answers started on {startedOnText(savedProgress) || "an earlier day"}
                  {firstNameOf(savedProgress) ? ` for ${firstNameOf(savedProgress)}` : ""}.
                </CardDescription>
              </CardHeader>
            ) : (
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Save className="h-5 w-5 text-blue-500" />
                  Previous Progress Found
                </CardTitle>
                <CardDescription>
                  You have saved progress for this assessment from{' '}
                  {savedProgress?.savedAt 
                    ? new Date(savedProgress.savedAt).toLocaleString() 
                    : 'a previous session'}
                  .
                  <br />
                  <strong>{Object.keys(savedProgress?.responses || {}).length} questions</strong> were answered.
                </CardDescription>
              </CardHeader>
            )}
            <CardContent className="restore-prompt-actions">
              <Button 
                onClick={restoreProgress} 
                className="restore-btn"
                data-testid="restore-progress-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {deviceDraft && !deviceDraft.linkKeyed ? "Continue" : "Continue Where I Left Off"}
              </Button>
              <Button 
                variant="outline" 
                onClick={startFresh}
                data-testid="start-fresh-btn"
              >
                {deviceDraft && !deviceDraft.linkKeyed ? "Start fresh" : "Start Fresh"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <LeaveFormDialog
        open={showLeaveConfirm}
        onStay={() => setShowLeaveConfirm(false)}
        onLeaveKeep={() => leaveForm(true)}
        onLeaveClear={() => leaveForm(false)}
      />

      {page === "admin" && (
        <AdminDashboard onBack={() => {
          setPage('start');
          window.history.pushState({}, '', window.location.pathname);
        }} />
      )}
      
      {page === "start" && (
        INTAKE_SLUGS.includes(preselectedAssessment) ? (
          <IntakeLanding slug={preselectedAssessment} onStart={startIntake} loading={loading} />
        ) : (
        <StartPage
          name={name}
          setName={setName}
          completedByName={completedByName}
          setCompletedByName={setCompletedByName}
          date={date}
          setDate={setDate}
          selectedAssessment={selectedAssessment}
          setSelectedAssessment={setSelectedAssessment}
          preselectedAssessment={preselectedAssessment}
          preselectedLabel={preselectedLabel}
          loadingAssessments={loadingAssessments}
          assessmentTypes={assessmentTypes}
          loading={loading}
          startAssessment={startAssessment}
          clientCode={clientCode}
          setClientCode={setClientCode}
          noCode={noCode}
          setNoCode={setNoCode}
          codeRequired={codeRequired}
          codePrefilled={codePrefilled}
          onCodeChange={() => setCodePrefilled(false)}
        />
        )
      )}

      {page === "assessment" && (
        <div className="assessment-page">
          {assessmentConfig ? (
            <>
              <div className="assessment-header">
                <h2 className="assessment-title">{assessmentConfig.name}</h2>
                <p className="assessment-instructions" style={{ whiteSpace: 'pre-line' }}>{(assessmentConfig.instructions || '').replace(/\\n/g, '\n')}</p>
                {/* Auto-save indicator */}
                {/* Auto-save indicator - more prominent */}
                <div className={`autosave-indicator ${lastSaved && draftId ? 'saved' : ''}`} data-testid="autosave-indicator">
                  {savingToServer ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving progress...</span>
                    </>
                  ) : lastSaved && draftId ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      <span>✓ Progress saved at {lastSaved.toLocaleTimeString()}</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Your progress will be auto-saved</span>
                    </>
                  )}
                </div>
              </div>

              <div className="questions-container">
                {/* Custom form components for v2 assessments */}
                {(!assessmentConfig.is_intake_form && showNamesPage) ? (
                  <Card className="question-card" data-testid="names-page">
                    <CardContent className="question-content">
                      <p className="question-text" style={{ marginBottom: "1.5rem" }}>First, a couple of quick details</p>
                      <div style={{ marginBottom: "1.25rem" }}>
                        <label htmlFor="names-assessed" style={{ display: "block", fontWeight: 600, fontSize: "0.9375rem", color: "var(--text-primary)", marginBottom: "0.375rem" }}>
                          Who is this about?
                        </label>
                        <p style={{ fontSize: "0.85rem", color: "#5a6b85", margin: "0 0 0.5rem" }}>
                          The person being assessed. If you're a parent, put your child's name here, not yours.
                        </p>
                        <Input
                          id="names-assessed"
                          data-testid="names-assessed-input"
                          type="text"
                          placeholder="Full name of the person being assessed"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid rgba(74, 144, 181, 0.2)", fontSize: "0.9375rem", backgroundColor: "#f8fafc", color: "#1a202c" }}
                        />
                      </div>
                      <div>
                        <label htmlFor="names-completer" style={{ display: "block", fontWeight: 600, fontSize: "0.9375rem", color: "var(--text-primary)", marginBottom: "0.375rem" }}>
                          Who is filling this in?
                        </label>
                        <p style={{ fontSize: "0.85rem", color: "#5a6b85", margin: "0 0 0.5rem" }}>Your own full name.</p>
                        <Input
                          id="names-completer"
                          data-testid="names-completer-input"
                          type="text"
                          placeholder="Your full name"
                          value={completedByName}
                          onChange={(e) => setCompletedByName(e.target.value)}
                          style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid rgba(74, 144, 181, 0.2)", fontSize: "0.9375rem", backgroundColor: "#f8fafc", color: "#1a202c" }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ) : assessmentConfig.is_intake_form ? (
                  <IntakeConsentForm
                    schema={assessmentConfig.form_schema}
                    responses={responses}
                    setResponses={setResponses}
                    agreementToken={agreementToken}
                    clientRef={clientReference}
                    codeRequired={codeRequired}
                    onSubmitSuccess={(data) => {
                      clearSavedProgress(selectedAssessment);
                      setDraftId(null);
                      setName(data.name || name);
                      setDate(data.date || date);
                      setResults({
                        name: data.name || name,
                        date: data.date || date,
                        assessment_type: selectedAssessment,
                        scores: data.scores || { is_qualitative: true },
                        interpretation: data.interpretation || { is_qualitative: true },
                        clinical_interpretation: data.clinical_interpretation || "",
                      });
                      setPage("results");
                    }}
                  />
                ) : selectedAssessment === 'ASD-Observation-Capture-v2' ? (
                  <ASDObservationForm responses={responses} setResponses={setResponses} />
                ) : selectedAssessment === 'Universal-Observation-Capture-v2' ? (
                  <UniversalObservationForm responses={responses} setResponses={setResponses} />
                ) : selectedAssessment === 'DASI2-Synthesis-v2' ? (
                  <DASI2SynthesisForm responses={responses} setResponses={setResponses} />
                ) : selectedAssessment === 'SLD-Academic-Observation-v2' ? (
                  <SLDAcademicForm responses={responses} setResponses={setResponses} />
                ) : selectedAssessment === 'Neuro-Dev-Pre-v2' ? (
                  <NeuroDevPreForm responses={responses} setResponses={setResponses} />
                ) : selectedAssessment === 'ADHD-Screener' ? (
                  <ADHDScreenerForm config={assessmentConfig} responses={responses} setResponses={setResponses} />
                ) : selectedAssessment === 'DASI-IR' ? (
                  <DASIInformantForm responses={responses} setResponses={setResponses} />
                ) : selectedAssessment === 'Understanding-Your-Learning' ? (
                  <UnderstandingLearningForm responses={responses} setResponses={setResponses} />
                ) : selectedAssessment === 'Neuro-Dev-Pre-Adult' ? (
                  <NeuroDevPreAdultForm responses={responses} setResponses={setResponses} />
                ) : selectedAssessment === 'ACE-SR-Adults' ? (
                  <ACESRAdultsForm responses={responses} setResponses={setResponses} />
                ) : selectedAssessment === 'ACEplus-Informant-Childhood' ? (
                  <ACEplusInformantForm responses={responses} setResponses={setResponses} variant="childhood" />
                ) : selectedAssessment === 'ACEplus-Informant-Adult' ? (
                  <ACEplusInformantForm responses={responses} setResponses={setResponses} variant="adult" />
                ) : selectedAssessment === 'ACE-Informant' ? (
                  <ACEInformantForm responses={responses} setResponses={setResponses} />
                ) : selectedAssessment === 'File-Upload' ? (
                  <FileUploadForm
                    responses={responses}
                    setResponses={setResponses}
                    clientName={name}
                    completedBy={completedByName}
                    date={date}
                    onUploadComplete={(result) => {
                      // After upload, the form stays to allow more uploads
                    }}
                  />
                ) : assessmentConfig.items.map((item) => {
                  // Conditional visibility: hide items that require an OR domain not selected in A1
                  if (item.requires_or && !(responses['A1'] || []).includes(item.requires_or)) {
                    return null;
                  }
                  // Evidence-dependency: block criterion endorsement when required OR evidence is missing
                  if (item.requires_evidence_from) {
                    const hasEvidence = item.requires_evidence_from.some(id => {
                      const val = responses[id];
                      return val !== undefined && val !== null && val !== '' && (!Array.isArray(val) || val.length > 0);
                    });
                    if (!hasEvidence) {
                      // For section/subsection headers, show them with a warning
                      if (item.input_type === 'subsection_header') {
                        return (
                          <div key={item.id} style={{
                            padding: '12px 16px',
                            marginBottom: '12px',
                            backgroundColor: 'rgba(245, 158, 11, 0.08)',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                            borderRadius: '8px',
                            color: '#92400e',
                            fontSize: '0.875rem',
                            fontStyle: 'italic'
                          }} data-testid={`evidence-blocked-${item.id}`}>
                            {item.text}. <strong>Complete the required observational record sections above to enable endorsement.</strong>
                          </div>
                        );
                      }
                      // For regular items, show locked state
                      return (
                        <div key={item.id} className="question-card" style={{ opacity: 0.5, pointerEvents: 'none' }} data-testid={`evidence-blocked-${item.id}`}>
                          <div className="question-label">
                            <span className="question-number">{item.id.replace(/_/g, ' ').toUpperCase()}</span>
                            <p>{item.text}</p>
                          </div>
                          <div style={{
                            padding: '10px 14px',
                            backgroundColor: 'rgba(245, 158, 11, 0.08)',
                            border: '1px dashed rgba(245, 158, 11, 0.4)',
                            borderRadius: '8px',
                            color: '#92400e',
                            fontSize: '0.8125rem',
                            fontStyle: 'italic',
                            textAlign: 'center'
                          }}>
                            Insufficient observational evidence: complete the required observational record sections first.
                          </div>
                        </div>
                      );
                    }
                  }
                  return (
                  item.input_type === "section_header" ? (
                    <div 
                      key={item.id}
                      style={{
                        borderTop: '3px solid #4A90B5',
                        marginTop: '32px',
                        paddingTop: '20px',
                        marginBottom: '16px'
                      }}
                      data-testid={`section-${item.id}`}
                    >
                      <h2 style={{
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        color: '#1e293b',
                        margin: 0
                      }}>
                        {item.text}
                      </h2>
                    </div>
                  ) : item.input_type === "subsection_header" ? (
                    <div 
                      key={item.id}
                      style={{
                        marginTop: '24px',
                        marginBottom: '12px',
                        paddingLeft: '8px',
                        borderLeft: '3px solid #94a3b8'
                      }}
                      data-testid={`subsection-${item.id}`}
                    >
                      <h3 style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        color: '#475569',
                        margin: 0
                      }}>
                        {item.text}
                      </h3>
                    </div>
                  ) : (
                  <Card 
                    key={item.id} 
                    className={`question-card ${incompleteItems.includes(item.id) ? 'incomplete' : ''}`}
                    data-testid={`question-${item.id}`}
                  >
                    <CardContent className="question-content">
                      <div className="question-header">
                        <span className="question-number">{typeof item.id === 'number' ? `Question ${item.id}` : ''}</span>
                        {incompleteItems.includes(item.id) && (
                          <span className="incomplete-badge">Please answer this question</span>
                        )}
                        {checkAnswerIds.includes(String(item.id)) && (
                          <span className="incomplete-badge" data-testid={`check-answer-${item.id}`}>Please check this answer</span>
                        )}
                      </div>
                      <p className="question-text" 
                         dangerouslySetInnerHTML={{ 
                           __html: item.text
                             .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                             .replace(/\n/g, '<br/>') 
                         }} 
                      />

                      {item.type === "number" ? (
                        <Input
                          type="number"
                          min="0"
                          max="30"
                          placeholder="Enter number"
                          value={responses[item.id] || ""}
                          onChange={(e) => {
                            setResponses({...responses, [item.id]: parseInt(e.target.value) || 0});
                            setIncompleteItems(prev => prev.filter(id => id !== item.id));
                          }}
                          className="number-input"
                          data-testid={`response-input-${item.id}`}
                        />
                      ) : item.input_type === "date" ? (
                        <DayFirstDate
                          label={item.text}
                          notFuture={isBirthDate(item.text) || isBirthDate(item.id)}
                          value={responses[item.id] || ""}
                          onChange={(newDate) => {
                            const newResponses = {...responses, [item.id]: newDate};
                            
                            // Auto-calculate age for any calculated_age fields that reference this date field
                            if (assessmentConfig && newDate) {
                              const birthDate = new Date(newDate);
                              const today = new Date();
                              let years = today.getFullYear() - birthDate.getFullYear();
                              let months = today.getMonth() - birthDate.getMonth();
                              
                              if (months < 0 || (months === 0 && today.getDate() < birthDate.getDate())) {
                                years--;
                                months += 12;
                              }
                              if (today.getDate() < birthDate.getDate()) {
                                months--;
                                if (months < 0) months += 12;
                              }
                              
                              const ageText = `${years} years${months > 0 ? `, ${months} months` : ''}`;
                              
                              // Find any calculated_age fields that reference this field
                              assessmentConfig.items.forEach(otherItem => {
                                if (otherItem.input_type === 'calculated_age' && otherItem.source_field === item.id) {
                                  newResponses[otherItem.id] = ageText;
                                }
                              });
                            }
                            
                            setResponses(newResponses);
                            setIncompleteItems(prev => prev.filter(id => id !== item.id));
                          }}
                          inputClassName="date-input"
                          inputStyle={{
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(74, 144, 181, 0.2)',
                            fontSize: '0.9375rem',
                            fontFamily: 'inherit',
                            backgroundColor: '#f8fafc',
                            color: '#1a202c'
                          }}
                          testId={`response-input-${item.id}`}
                        />
                      ) : item.input_type === "calculated_age" ? (
                        <Input
                          type="text"
                          value={responses[item.id] || "(Will be calculated from Date of Birth)"}
                          readOnly
                          className="calculated-age-input"
                          style={{
                            width: '100%',
                            maxWidth: '300px',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(74, 144, 181, 0.2)',
                            fontSize: '0.9375rem',
                            fontFamily: 'inherit',
                            backgroundColor: responses[item.id] ? '#e8f5e9' : '#f0f0f0',
                            color: responses[item.id] ? '#2e7d32' : '#888',
                            fontWeight: responses[item.id] ? '500' : 'normal'
                          }}
                          data-testid={`response-input-${item.id}`}
                        />
                      ) : item.input_type === "section_header" ? (
                        <div style={{
                          borderTop: '2px solid #4A90B5',
                          marginTop: '24px',
                          paddingTop: '16px'
                        }}>
                          {/* Section header - no input needed */}
                        </div>
                      ) : item.input_type === "subsection_header" ? (
                        <div style={{
                          marginTop: '16px',
                          paddingTop: '8px'
                        }}>
                          {/* Subsection header - no input needed */}
                        </div>
                      ) : item.input_type === "checkbox" ? (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '8px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`checkbox-${item.id}`}
                                checked={responses[item.id]?.answer === 'Yes'}
                                onChange={() => {
                                  setResponses({
                                    ...responses, 
                                    [item.id]: {
                                      ...responses[item.id],
                                      answer: 'Yes'
                                    }
                                  });
                                  setIncompleteItems(prev => prev.filter(id => id !== item.id));
                                }}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  cursor: 'pointer',
                                  accentColor: '#4A90B5'
                                }}
                                data-testid={`response-yes-${item.id}`}
                              />
                              <span style={{ 
                                color: responses[item.id]?.answer === 'Yes' ? '#4A90B5' : '#64748b',
                                fontWeight: responses[item.id]?.answer === 'Yes' ? '600' : '400'
                              }}>
                                Yes
                              </span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`checkbox-${item.id}`}
                                checked={responses[item.id]?.answer === 'No'}
                                onChange={() => {
                                  setResponses({
                                    ...responses, 
                                    [item.id]: {
                                      ...responses[item.id],
                                      answer: 'No',
                                      notes: ''
                                    }
                                  });
                                  setIncompleteItems(prev => prev.filter(id => id !== item.id));
                                }}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  cursor: 'pointer',
                                  accentColor: '#4A90B5'
                                }}
                                data-testid={`response-no-${item.id}`}
                              />
                              <span style={{ 
                                color: responses[item.id]?.answer === 'No' ? '#4A90B5' : '#64748b',
                                fontWeight: responses[item.id]?.answer === 'No' ? '600' : '400'
                              }}>
                                No
                              </span>
                            </label>
                          </div>
                          {responses[item.id]?.answer === 'Yes' && (
                            <textarea
                              placeholder="Add elaboration if needed..."
                              value={responses[item.id]?.notes || ""}
                              onChange={(e) => {
                                setResponses({
                                  ...responses, 
                                  [item.id]: {
                                    ...responses[item.id],
                                    notes: e.target.value
                                  }
                                });
                              }}
                              className="text-input"
                              rows="2"
                              style={{
                                width: '100%',
                                marginTop: '8px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.875rem'
                              }}
                              data-testid={`response-checkbox-notes-${item.id}`}
                            />
                          )}
                        </div>
                      ) : item.input_type === "slider" ? (
                        <div style={{ padding: '20px 0' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '12px'
                          }}>
                            <span style={{ fontSize: '0.875rem', color: '#64748b' }}>{item.min || 1} - Really tough</span>
                            <span style={{ 
                              fontSize: '2rem', 
                              fontWeight: '700', 
                              color: responses[item.id] ? '#4A90B5' : '#cbd5e1',
                              minWidth: '60px',
                              textAlign: 'center'
                            }}>
                              {responses[item.id] || '-'}
                            </span>
                            <span style={{ fontSize: '0.875rem', color: '#64748b' }}>{item.max || 10} - Awesome</span>
                          </div>
                          <input
                            type="range"
                            min={item.min || 1}
                            max={item.max || 10}
                            value={responses[item.id] || Math.floor(((item.min || 1) + (item.max || 10)) / 2)}
                            onChange={(e) => {
                              setResponses({...responses, [item.id]: parseInt(e.target.value)});
                              setIncompleteItems(prev => prev.filter(id => id !== item.id));
                            }}
                            style={{
                              width: '100%',
                              height: '8px',
                              borderRadius: '4px',
                              background: `linear-gradient(to right, #4A90B5 0%, #4A90B5 ${((responses[item.id] || 5) - (item.min || 1)) / ((item.max || 10) - (item.min || 1)) * 100}%, #e2e8f0 ${((responses[item.id] || 5) - (item.min || 1)) / ((item.max || 10) - (item.min || 1)) * 100}%, #e2e8f0 100%)`,
                              cursor: 'pointer',
                              WebkitAppearance: 'none',
                              appearance: 'none'
                            }}
                            data-testid={`response-slider-${item.id}`}
                          />
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            marginTop: '8px',
                            padding: '0 4px'
                          }}>
                            {[...Array((item.max || 10) - (item.min || 1) + 1)].map((_, i) => (
                              <span 
                                key={i} 
                                style={{ 
                                  fontSize: '0.75rem', 
                                  color: responses[item.id] === (item.min || 1) + i ? '#4A90B5' : '#94a3b8',
                                  fontWeight: responses[item.id] === (item.min || 1) + i ? '600' : '400'
                                }}
                              >
                                {(item.min || 1) + i}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : item.input_type === "text" ? (
                        <input
                          type="text"
                          placeholder={item.text.includes('Name') ? 'Enter name' : 'Enter response'}
                          value={responses[item.id] || ""}
                          onChange={(e) => {
                            setResponses({...responses, [item.id]: e.target.value});
                            setIncompleteItems(prev => prev.filter(id => id !== item.id));
                          }}
                          className="text-input"
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(74, 144, 181, 0.2)',
                            fontSize: '0.9375rem',
                            fontFamily: 'inherit',
                            backgroundColor: '#f8fafc',
                            color: '#1a202c'
                          }}
                          data-testid={`response-input-${item.id}`}
                        />
                      ) : item.input_type === "textarea" ? (
                        <textarea
                          placeholder="Please provide detailed observations..."
                          value={responses[item.id] || ""}
                          onChange={(e) => {
                            setResponses({...responses, [item.id]: e.target.value});
                            setIncompleteItems(prev => prev.filter(id => id !== item.id));
                          }}
                          className="text-input"
                          rows="4"
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(74, 144, 181, 0.2)',
                            fontSize: '0.9375rem',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            minHeight: '100px',
                            backgroundColor: '#f8fafc',
                            color: '#1a202c'
                          }}
                          data-testid={`response-input-${item.id}`}
                        />
                      ) : item.input_type === "options_with_textarea" && item.options ? (
                        <div className="options-with-textarea">
                          {/* Rating Options */}
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ 
                              fontSize: '0.875rem', 
                              fontWeight: '600', 
                              color: '#4A90B5', 
                              marginBottom: '8px' 
                            }}>
                              Rating:
                            </div>
                            <RadioGroup
                              value={responses[`${item.id}_rating`]?.toString() || ""}
                              onValueChange={(value) => {
                                setResponses({...responses, [`${item.id}_rating`]: value});
                                setIncompleteItems(prev => prev.filter(id => id !== item.id));
                              }}
                              className="radio-group"
                            >
                              {item.options.map((option) => (
                                <div 
                                  key={option.value} 
                                  className={`radio-option clickable ${responses[`${item.id}_rating`]?.toString() === option.value.toString() ? 'selected' : ''}`}
                                  data-testid={`response-option-${item.id}-${option.value}`}
                                  onClick={() => {
                                    setResponses({...responses, [`${item.id}_rating`]: option.value});
                                    setIncompleteItems(prev => prev.filter(id => id !== item.id));
                                  }}
                                >
                                  <RadioGroupItem
                                    value={option.value.toString()}
                                    id={`q${item.id}-${option.value}`}
                                    className="radio-input"
                                  />
                                  <Label
                                    htmlFor={`q${item.id}-${option.value}`}
                                    className="radio-label"
                                  >
                                    {option.label}
                                  </Label>
                                </div>
                              ))}
                            </RadioGroup>
                          </div>
                          {/* Observations Textarea(s) */}
                          {item.split_textarea ? (
                            <>
                              <div style={{ marginTop: '8px' }}>
                                <div style={{ 
                                  fontSize: '0.875rem', 
                                  fontWeight: '600', 
                                  color: '#4A90B5', 
                                  marginBottom: '8px' 
                                }}>
                                  Client quotes:
                                </div>
                                <textarea
                                  placeholder="Record relevant client quotes using quotation marks..."
                                  value={responses[`${item.id}_client_quotes`] || ""}
                                  onChange={(e) => {
                                    setResponses({...responses, [`${item.id}_client_quotes`]: e.target.value});
                                  }}
                                  className="text-input"
                                  rows="3"
                                  style={{
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(74, 144, 181, 0.2)',
                                    fontSize: '0.9375rem',
                                    fontFamily: 'inherit',
                                    resize: 'vertical',
                                    minHeight: '80px',
                                    backgroundColor: '#f8fafc',
                                    color: '#1a202c'
                                  }}
                                  data-testid={`response-input-${item.id}-client-quotes`}
                                />
                              </div>
                              <div style={{ marginTop: '12px' }}>
                                <div style={{ 
                                  fontSize: '0.875rem', 
                                  fontWeight: '600', 
                                  color: '#4A90B5', 
                                  marginBottom: '8px' 
                                }}>
                                  Clinician observations:
                                </div>
                                <textarea
                                  placeholder="Record your clinical observations, noting quality, context, and patterns..."
                                  value={responses[`${item.id}_clinician_obs`] || ""}
                                  onChange={(e) => {
                                    setResponses({...responses, [`${item.id}_clinician_obs`]: e.target.value});
                                  }}
                                  className="text-input"
                                  rows="3"
                                  style={{
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(74, 144, 181, 0.2)',
                                    fontSize: '0.9375rem',
                                    fontFamily: 'inherit',
                                    resize: 'vertical',
                                    minHeight: '80px',
                                    backgroundColor: '#f8fafc',
                                    color: '#1a202c'
                                  }}
                                  data-testid={`response-input-${item.id}-clinician-obs`}
                                />
                              </div>
                            </>
                          ) : (
                          <div>
                            <div style={{ 
                              fontSize: '0.875rem', 
                              fontWeight: '600', 
                              color: '#4A90B5', 
                              marginBottom: '8px' 
                            }}>
                              {item.textarea_label || "Observations:"}
                            </div>
                            <textarea
                              placeholder={item.textarea_placeholder || "Enter your observations..."}
                              value={responses[`${item.id}_text`] || ""}
                              onChange={(e) => {
                                setResponses({...responses, [`${item.id}_text`]: e.target.value});
                              }}
                              className="text-input"
                              rows="4"
                              style={{
                                width: '100%',
                                padding: '12px',
                                borderRadius: '8px',
                                border: '1px solid rgba(74, 144, 181, 0.2)',
                                fontSize: '0.9375rem',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                minHeight: '100px',
                                backgroundColor: '#f8fafc',
                                color: '#1a202c'
                              }}
                              data-testid={`response-input-${item.id}-text`}
                            />
                          </div>
                          )}
                        </div>
                      ) : item.input_type === "checkboxes_with_textarea" && item.options ? (
                        <div className="checkboxes-with-textarea">
                          {/* Checkbox Options (multi-select) */}
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ 
                              fontSize: '0.875rem', 
                              fontWeight: '600', 
                              color: '#4A90B5', 
                              marginBottom: '8px' 
                            }}>
                              {item.checkbox_label || "Select all that apply:"}
                            </div>
                            <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {item.options.map((option) => {
                                const checkboxKey = `${item.id}_checks`;
                                const currentChecks = responses[checkboxKey] || [];
                                const isChecked = currentChecks.includes(option.value);
                                return (
                                  <div 
                                    key={option.value}
                                    className={`checkbox-option clickable ${isChecked ? 'selected' : ''}`}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '10px 14px',
                                      borderRadius: '8px',
                                      border: isChecked ? '2px solid #4A90B5' : '1px solid rgba(74, 144, 181, 0.2)',
                                      backgroundColor: isChecked ? 'rgba(74, 144, 181, 0.1)' : '#f8fafc',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => {
                                      const newChecks = isChecked 
                                        ? currentChecks.filter(v => v !== option.value)
                                        : [...currentChecks, option.value];
                                      setResponses({...responses, [checkboxKey]: newChecks});
                                      setIncompleteItems(prev => prev.filter(id => id !== item.id));
                                    }}
                                    data-testid={`response-checkbox-${item.id}-${option.value}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => {}}
                                      style={{
                                        width: '18px',
                                        height: '18px',
                                        marginRight: '12px',
                                        accentColor: '#4A90B5',
                                        cursor: 'pointer'
                                      }}
                                    />
                                    <span style={{ color: '#1a202c', fontSize: '0.9375rem' }}>
                                      {option.label}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          {/* Details Textarea */}
                          <div>
                            <div style={{ 
                              fontSize: '0.875rem', 
                              fontWeight: '600', 
                              color: '#4A90B5', 
                              marginBottom: '8px' 
                            }}>
                              {item.textarea_label || "Please provide details:"}
                            </div>
                            <textarea
                              placeholder="Enter details..."
                              value={responses[item.id] || ""}
                              onChange={(e) => {
                                setResponses({...responses, [item.id]: e.target.value});
                              }}
                              className="text-input"
                              rows="4"
                              style={{
                                width: '100%',
                                padding: '12px',
                                borderRadius: '8px',
                                border: '1px solid rgba(74, 144, 181, 0.2)',
                                fontSize: '0.9375rem',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                minHeight: '100px',
                                backgroundColor: '#f8fafc',
                                color: '#1a202c'
                              }}
                              data-testid={`response-input-${item.id}`}
                            />
                          </div>
                        </div>
                      ) : item.input_type === "multi_select" && item.options ? (
                        <div className="multi-select-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {item.options.map((option) => {
                            const currentSelections = responses[item.id] || [];
                            const isSelected = currentSelections.includes(option.value);
                            return (
                              <div
                                key={option.value}
                                className={`checkbox-option clickable ${isSelected ? 'selected' : ''}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '10px 14px',
                                  borderRadius: '8px',
                                  border: isSelected ? '2px solid #4A90B5' : '1px solid rgba(74, 144, 181, 0.2)',
                                  backgroundColor: isSelected ? 'rgba(74, 144, 181, 0.1)' : '#f8fafc',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                onClick={() => {
                                  const newSelections = isSelected
                                    ? currentSelections.filter(v => v !== option.value)
                                    : [...currentSelections, option.value];
                                  setResponses({...responses, [item.id]: newSelections});
                                  setIncompleteItems(prev => prev.filter(id => id !== item.id));
                                }}
                                data-testid={`response-multiselect-${item.id}-${option.value}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                    marginRight: '12px',
                                    accentColor: '#4A90B5',
                                    cursor: 'pointer'
                                  }}
                                />
                                <span style={{ color: '#1a202c', fontSize: '0.9375rem' }}>
                                  {option.label}
                                </span>
                              </div>
                            );
                          })}
                          {item.textarea_label && (
                            <div style={{ marginTop: '8px' }}>
                              <label style={{ fontSize: '0.85rem', color: '#555', fontWeight: 500, display: 'block', marginBottom: '4px' }}>
                                {item.textarea_label}
                              </label>
                              <textarea
                                value={responses[`${item.id}_notes`] || ''}
                                onChange={(e) => setResponses({...responses, [`${item.id}_notes`]: e.target.value})}
                                placeholder={item.textarea_placeholder || "Additional observations..."}
                                data-testid={`response-multiselect-notes-${item.id}`}
                                rows={3}
                                style={{
                                  width: '100%',
                                  padding: '10px 14px',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(74, 144, 181, 0.3)',
                                  fontSize: '0.9375rem',
                                  resize: 'vertical',
                                  backgroundColor: '#f8fafc'
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ) : item.input_type === "domain_count" ? (
                        (() => {
                          const count = (item.count_items || []).filter(id => responses[id] === item.count_value).length;
                          const total = item.total || 9;
                          const threshold = item.threshold || 6;
                          const meetsThreshold = count >= threshold;
                          return (
                            <div data-testid={`domain-count-${item.id}`} style={{
                              padding: '14px 18px',
                              borderRadius: '8px',
                              backgroundColor: meetsThreshold ? 'rgba(220, 38, 38, 0.08)' : 'rgba(74, 144, 181, 0.08)',
                              border: `2px solid ${meetsThreshold ? 'rgba(220, 38, 38, 0.3)' : 'rgba(74, 144, 181, 0.2)'}`,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{ fontWeight: 600, color: '#1a202c' }}>
                                Endorsed (Present): <span style={{ fontSize: '1.125rem', color: meetsThreshold ? '#dc2626' : '#4A90B5' }}>{count} / {total}</span>
                              </span>
                              <span style={{ fontSize: '0.8125rem', color: meetsThreshold ? '#dc2626' : '#6b7280', fontStyle: 'italic' }}>
                                {meetsThreshold ? `Meets threshold (≥${threshold})` : `Below threshold (≥${threshold} required)`}
                              </span>
                            </div>
                          );
                        })()
                      ) : item.input_type === "dsm5_mapping" ? (
                        <div className="dsm5-mapping">
                          {/* CRITERION A - SOCIAL COMMUNICATION & INTERACTION */}
                          <div style={{ 
                            marginBottom: '24px',
                            padding: '16px',
                            backgroundColor: '#f0f9ff',
                            borderRadius: '8px',
                            border: '1px solid rgba(74, 144, 181, 0.2)'
                          }}>
                            <div style={{ 
                              fontSize: '1rem', 
                              fontWeight: '700', 
                              color: '#2563eb', 
                              marginBottom: '16px' 
                            }}>
                              CRITERION A - SOCIAL COMMUNICATION & INTERACTION
                            </div>
                            
                            {/* A1 */}
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontWeight: '600', marginRight: '12px' }}>A1 (Social-emotional reciprocity):</span>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                  {[{value: 'yes', label: '✓'}, {value: 'no', label: '✗'}].map((opt) => (
                                    <div 
                                      key={opt.value}
                                      onClick={() => setResponses({...responses, [`${item.id}_A1_checked`]: opt.value})}
                                      style={{
                                        padding: '4px 12px',
                                        borderRadius: '4px',
                                        border: responses[`${item.id}_A1_checked`] === opt.value ? '2px solid #2563eb' : '1px solid #ccc',
                                        backgroundColor: responses[`${item.id}_A1_checked`] === opt.value ? 'rgba(37, 99, 235, 0.1)' : 'white',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                      }}
                                    >
                                      {opt.label}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <textarea
                                placeholder="Enter observations for A1..."
                                value={responses[`${item.id}_A1_notes`] || ""}
                                onChange={(e) => setResponses({...responses, [`${item.id}_A1_notes`]: e.target.value})}
                                rows="2"
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                              />
                            </div>
                            
                            {/* A2 */}
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontWeight: '600', marginRight: '12px' }}>A2 (Nonverbal communicative behaviors):</span>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                  {[{value: 'yes', label: '✓'}, {value: 'no', label: '✗'}].map((opt) => (
                                    <div 
                                      key={opt.value}
                                      onClick={() => setResponses({...responses, [`${item.id}_A2_checked`]: opt.value})}
                                      style={{
                                        padding: '4px 12px',
                                        borderRadius: '4px',
                                        border: responses[`${item.id}_A2_checked`] === opt.value ? '2px solid #2563eb' : '1px solid #ccc',
                                        backgroundColor: responses[`${item.id}_A2_checked`] === opt.value ? 'rgba(37, 99, 235, 0.1)' : 'white',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                      }}
                                    >
                                      {opt.label}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <textarea
                                placeholder="Enter observations for A2..."
                                value={responses[`${item.id}_A2_notes`] || ""}
                                onChange={(e) => setResponses({...responses, [`${item.id}_A2_notes`]: e.target.value})}
                                rows="2"
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                              />
                            </div>
                            
                            {/* A3 */}
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontWeight: '600', marginRight: '12px' }}>A3 (Developing, maintaining, understanding relationships):</span>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                  {[{value: 'yes', label: '✓'}, {value: 'no', label: '✗'}].map((opt) => (
                                    <div 
                                      key={opt.value}
                                      onClick={() => setResponses({...responses, [`${item.id}_A3_checked`]: opt.value})}
                                      style={{
                                        padding: '4px 12px',
                                        borderRadius: '4px',
                                        border: responses[`${item.id}_A3_checked`] === opt.value ? '2px solid #2563eb' : '1px solid #ccc',
                                        backgroundColor: responses[`${item.id}_A3_checked`] === opt.value ? 'rgba(37, 99, 235, 0.1)' : 'white',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                      }}
                                    >
                                      {opt.label}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <textarea
                                placeholder="Enter observations for A3..."
                                value={responses[`${item.id}_A3_notes`] || ""}
                                onChange={(e) => setResponses({...responses, [`${item.id}_A3_notes`]: e.target.value})}
                                rows="2"
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                              />
                            </div>
                          </div>
                          
                          {/* CRITERION B - RESTRICTED, REPETITIVE BEHAVIOURS */}
                          <div style={{ 
                            marginBottom: '24px',
                            padding: '16px',
                            backgroundColor: '#fef3c7',
                            borderRadius: '8px',
                            border: '1px solid rgba(217, 119, 6, 0.2)'
                          }}>
                            <div style={{ 
                              fontSize: '1rem', 
                              fontWeight: '700', 
                              color: '#d97706', 
                              marginBottom: '16px' 
                            }}>
                              CRITERION B - RESTRICTED, REPETITIVE BEHAVIOURS
                            </div>
                            
                            {/* B1 */}
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontWeight: '600', marginRight: '12px' }}>B1 (Stereotyped/repetitive motor movements, object use, or speech):</span>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                  {[{value: 'yes', label: '✓'}, {value: 'no', label: '✗'}].map((opt) => (
                                    <div 
                                      key={opt.value}
                                      onClick={() => setResponses({...responses, [`${item.id}_B1_checked`]: opt.value})}
                                      style={{
                                        padding: '4px 12px',
                                        borderRadius: '4px',
                                        border: responses[`${item.id}_B1_checked`] === opt.value ? '2px solid #d97706' : '1px solid #ccc',
                                        backgroundColor: responses[`${item.id}_B1_checked`] === opt.value ? 'rgba(217, 119, 6, 0.1)' : 'white',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                      }}
                                    >
                                      {opt.label}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <textarea
                                placeholder="Enter observations for B1..."
                                value={responses[`${item.id}_B1_notes`] || ""}
                                onChange={(e) => setResponses({...responses, [`${item.id}_B1_notes`]: e.target.value})}
                                rows="2"
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                              />
                            </div>
                            
                            {/* B2 */}
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontWeight: '600', marginRight: '12px' }}>B2 (Insistence on sameness, inflexible adherence to routines):</span>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                  {[{value: 'yes', label: '✓'}, {value: 'no', label: '✗'}].map((opt) => (
                                    <div 
                                      key={opt.value}
                                      onClick={() => setResponses({...responses, [`${item.id}_B2_checked`]: opt.value})}
                                      style={{
                                        padding: '4px 12px',
                                        borderRadius: '4px',
                                        border: responses[`${item.id}_B2_checked`] === opt.value ? '2px solid #d97706' : '1px solid #ccc',
                                        backgroundColor: responses[`${item.id}_B2_checked`] === opt.value ? 'rgba(217, 119, 6, 0.1)' : 'white',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                      }}
                                    >
                                      {opt.label}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <textarea
                                placeholder="Enter observations for B2..."
                                value={responses[`${item.id}_B2_notes`] || ""}
                                onChange={(e) => setResponses({...responses, [`${item.id}_B2_notes`]: e.target.value})}
                                rows="2"
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                              />
                            </div>
                            
                            {/* B3 */}
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontWeight: '600', marginRight: '12px' }}>B3 (Highly restricted, fixated interests):</span>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                  {[{value: 'yes', label: '✓'}, {value: 'no', label: '✗'}].map((opt) => (
                                    <div 
                                      key={opt.value}
                                      onClick={() => setResponses({...responses, [`${item.id}_B3_checked`]: opt.value})}
                                      style={{
                                        padding: '4px 12px',
                                        borderRadius: '4px',
                                        border: responses[`${item.id}_B3_checked`] === opt.value ? '2px solid #d97706' : '1px solid #ccc',
                                        backgroundColor: responses[`${item.id}_B3_checked`] === opt.value ? 'rgba(217, 119, 6, 0.1)' : 'white',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                      }}
                                    >
                                      {opt.label}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <textarea
                                placeholder="Enter observations for B3..."
                                value={responses[`${item.id}_B3_notes`] || ""}
                                onChange={(e) => setResponses({...responses, [`${item.id}_B3_notes`]: e.target.value})}
                                rows="2"
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                              />
                            </div>
                            
                            {/* B4 */}
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontWeight: '600', marginRight: '12px' }}>B4 (Hyper- or hyporeactivity to sensory input):</span>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                  {[{value: 'yes', label: '✓'}, {value: 'no', label: '✗'}].map((opt) => (
                                    <div 
                                      key={opt.value}
                                      onClick={() => setResponses({...responses, [`${item.id}_B4_checked`]: opt.value})}
                                      style={{
                                        padding: '4px 12px',
                                        borderRadius: '4px',
                                        border: responses[`${item.id}_B4_checked`] === opt.value ? '2px solid #d97706' : '1px solid #ccc',
                                        backgroundColor: responses[`${item.id}_B4_checked`] === opt.value ? 'rgba(217, 119, 6, 0.1)' : 'white',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                      }}
                                    >
                                      {opt.label}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <textarea
                                placeholder="Enter observations for B4..."
                                value={responses[`${item.id}_B4_notes`] || ""}
                                onChange={(e) => setResponses({...responses, [`${item.id}_B4_notes`]: e.target.value})}
                                rows="2"
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                              />
                            </div>
                          </div>
                          
                          {/* ONSET HISTORY */}
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ fontSize: '0.9375rem', fontWeight: '700', color: '#1a202c', marginBottom: '8px' }}>
                              ONSET HISTORY:
                            </div>
                            <textarea
                              placeholder="Document onset history..."
                              value={responses[`${item.id}_onset`] || ""}
                              onChange={(e) => setResponses({...responses, [`${item.id}_onset`]: e.target.value})}
                              rows="3"
                              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                            />
                          </div>
                          
                          {/* FUNCTIONAL IMPACT */}
                          <div>
                            <div style={{ fontSize: '0.9375rem', fontWeight: '700', color: '#1a202c', marginBottom: '8px' }}>
                              FUNCTIONAL IMPACT:
                            </div>
                            <textarea
                              placeholder="Document functional impact..."
                              value={responses[`${item.id}_functional`] || ""}
                              onChange={(e) => setResponses({...responses, [`${item.id}_functional`]: e.target.value})}
                              rows="3"
                              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                            />
                          </div>
                        </div>
                      ) : item.input_type === "weekly_mood_table" ? (
                        <div className="weekly-mood-table" style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                          <table style={{ 
                            width: '100%', 
                            borderCollapse: 'collapse', 
                            fontSize: '0.875rem',
                            backgroundColor: 'white'
                          }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                              <tr style={{ backgroundColor: '#f8fafc' }}>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #e2e8f0', width: '100px', backgroundColor: '#f8fafc' }}>Day</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #e2e8f0', width: '180px', backgroundColor: '#f8fafc' }}>
                                  Big Feelings (0-10)
                                  <div style={{ fontSize: '0.7rem', fontWeight: 'normal', color: '#64748b', marginTop: '2px' }}>
                                    0=Calm, 5=Some big feelings, 10=Lots of big feelings
                                  </div>
                                </th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' }}>What did the big feelings look like?</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' }}>What was happening at the time?</th>
                                {item.include_comments && (
                                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' }}>Anything else you noticed?</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, idx) => (
                                <tr key={day} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', fontWeight: '500' }}>{day}</td>
                                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>
                                    <select
                                      value={responses[`${item.id}_${day.toLowerCase()}_rating`] || ''}
                                      onChange={(e) => setResponses({...responses, [`${item.id}_${day.toLowerCase()}_rating`]: e.target.value})}
                                      style={{ 
                                        width: '100%', 
                                        padding: '8px', 
                                        borderRadius: '6px', 
                                        border: '1px solid #cbd5e1',
                                        backgroundColor: 'white',
                                        fontSize: '0.875rem'
                                      }}
                                    >
                                      <option value="">Select...</option>
                                      <option value="0">0 - Calm & settled</option>
                                      <option value="1">1</option>
                                      <option value="2">2</option>
                                      <option value="3">3</option>
                                      <option value="4">4</option>
                                      <option value="5">5 - Some big feelings</option>
                                      <option value="6">6</option>
                                      <option value="7">7</option>
                                      <option value="8">8</option>
                                      <option value="9">9</option>
                                      <option value="10">10 - Lots of big feelings</option>
                                    </select>
                                  </td>
                                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>
                                    <textarea
                                      placeholder="e.g., crying, needing space, getting loud..."
                                      value={responses[`${item.id}_${day.toLowerCase()}_dysregulation`] || ''}
                                      onChange={(e) => setResponses({...responses, [`${item.id}_${day.toLowerCase()}_dysregulation`]: e.target.value})}
                                      rows="2"
                                      style={{ 
                                        width: '100%', 
                                        padding: '8px', 
                                        borderRadius: '6px', 
                                        border: '1px solid #cbd5e1',
                                        fontSize: '0.875rem',
                                        resize: 'vertical'
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>
                                    <textarea
                                      placeholder="e.g., transitions, unexpected changes..."
                                      value={responses[`${item.id}_${day.toLowerCase()}_context`] || ''}
                                      onChange={(e) => setResponses({...responses, [`${item.id}_${day.toLowerCase()}_context`]: e.target.value})}
                                      rows="2"
                                      style={{ 
                                        width: '100%', 
                                        padding: '8px', 
                                        borderRadius: '6px', 
                                        border: '1px solid #cbd5e1',
                                        fontSize: '0.875rem',
                                        resize: 'vertical'
                                      }}
                                    />
                                  </td>
                                  {item.include_comments && (
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>
                                      <textarea
                                        placeholder="Anything else you noticed..."
                                        value={responses[`${item.id}_${day.toLowerCase()}_comments`] || ''}
                                        onChange={(e) => setResponses({...responses, [`${item.id}_${day.toLowerCase()}_comments`]: e.target.value})}
                                        rows="2"
                                        style={{ 
                                          width: '100%', 
                                          padding: '8px', 
                                          borderRadius: '6px', 
                                          border: '1px solid #cbd5e1',
                                          fontSize: '0.875rem',
                                          resize: 'vertical'
                                        }}
                                      />
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : item.input_type === "weekly_mood_table_parent" ? (
                        <div className="weekly-mood-table-parent" style={{ borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', backgroundColor: 'white' }}>
                          {/* Mobile-optimized card layout for Parent Mood Table */}
                          <div className="mood-entries">
                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, idx) => (
                              <div key={day} style={{ 
                                padding: '16px', 
                                borderBottom: '1px solid #e2e8f0',
                                backgroundColor: idx % 2 === 0 ? 'white' : '#f8fafc'
                              }}>
                                <div style={{ fontWeight: '600', color: '#1e3a5f', fontSize: '0.95rem', marginBottom: '12px' }}>{day}</div>
                                
                                {/* Big Feelings Rating */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>
                                    Big Feelings Rating (0-10)
                                    <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 'normal', color: '#64748b' }}>
                                      0 = Calm & settled, 5 = Some big feelings, 10 = Lots of big feelings
                                    </span>
                                  </label>
                                  <select
                                    value={responses[`${item.id}_${day.toLowerCase()}_rating`] || ''}
                                    onChange={(e) => setResponses({...responses, [`${item.id}_${day.toLowerCase()}_rating`]: e.target.value})}
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      backgroundColor: 'white',
                                      fontSize: '0.9rem'
                                    }}
                                  >
                                    <option value="">Select...</option>
                                    <option value="0">0 - Calm & settled</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5 - Some big feelings</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10 - Lots of big feelings</option>
                                  </select>
                                </div>
                                
                                {/* What did the big feelings look like */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>
                                    What did the big feelings look like?
                                  </label>
                                  <textarea
                                    placeholder="e.g., crying, needing quiet time, getting loud..."
                                    value={responses[`${item.id}_${day.toLowerCase()}_emotion_look`] || ''}
                                    onChange={(e) => setResponses({...responses, [`${item.id}_${day.toLowerCase()}_emotion_look`]: e.target.value})}
                                    rows="2"
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      fontSize: '0.9rem',
                                      resize: 'vertical',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                                
                                {/* Context - long day or specific event */}
                                <div style={{ marginBottom: '4px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>
                                    Was it a big day, or did something specific happen?
                                  </label>
                                  <textarea
                                    placeholder="e.g., long day at school, change in routine, something exciting..."
                                    value={responses[`${item.id}_${day.toLowerCase()}_context`] || ''}
                                    onChange={(e) => setResponses({...responses, [`${item.id}_${day.toLowerCase()}_context`]: e.target.value})}
                                    rows="2"
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      fontSize: '0.9rem',
                                      resize: 'vertical',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : item.input_type === "weekly_event_table" ? (
                        <div className="weekly-event-table" style={{ borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', backgroundColor: 'white' }}>
                          {/* Mobile-optimized card layout */}
                          <div className="event-entries">
                            {(responses[`${item.id}_events`] || [{ day: '', event: '', behaviour: '', emotion: '', whatHelped: '', whatDidNotHelp: '' }]).map((event, idx) => (
                              <div key={idx} style={{ 
                                padding: '16px', 
                                borderBottom: '1px solid #e2e8f0',
                                backgroundColor: idx % 2 === 0 ? 'white' : '#f8fafc'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                  <span style={{ fontWeight: '600', color: '#1e3a5f', fontSize: '0.9rem' }}>Entry {idx + 1}</span>
                                  {(responses[`${item.id}_events`] || []).length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const events = [...(responses[`${item.id}_events`] || [])];
                                        events.splice(idx, 1);
                                        setResponses({...responses, [`${item.id}_events`]: events});
                                      }}
                                      style={{
                                        padding: '4px 10px',
                                        backgroundColor: '#fee2e2',
                                        color: '#dc2626',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                                
                                {/* Day selector */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>Day</label>
                                  <select
                                    value={event.day || ''}
                                    onChange={(e) => {
                                      const events = [...(responses[`${item.id}_events`] || [{ day: '', event: '', behaviour: '', emotion: '', whatHelped: '', whatDidNotHelp: '' }])];
                                      events[idx] = { ...events[idx], day: e.target.value };
                                      setResponses({...responses, [`${item.id}_events`]: events});
                                    }}
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      backgroundColor: 'white',
                                      fontSize: '0.9rem'
                                    }}
                                  >
                                    <option value="">Select day...</option>
                                    <option value="Monday">Monday</option>
                                    <option value="Tuesday">Tuesday</option>
                                    <option value="Wednesday">Wednesday</option>
                                    <option value="Thursday">Thursday</option>
                                    <option value="Friday">Friday</option>
                                    <option value="Saturday">Saturday</option>
                                    <option value="Sunday">Sunday</option>
                                  </select>
                                </div>
                                
                                {/* What was happening */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>What was happening?</label>
                                  <textarea
                                    placeholder="e.g., getting ready for school, at the shops, homework time..."
                                    value={event.event || ''}
                                    onChange={(e) => {
                                      const events = [...(responses[`${item.id}_events`] || [{ day: '', event: '', behaviour: '', emotion: '', whatHelped: '', whatDidNotHelp: '' }])];
                                      events[idx] = { ...events[idx], event: e.target.value };
                                      setResponses({...responses, [`${item.id}_events`]: events});
                                    }}
                                    rows="2"
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      fontSize: '0.9rem',
                                      resize: 'vertical',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                                
                                {/* What did you notice */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>What did you notice?</label>
                                  <textarea
                                    placeholder="e.g., needed to move around, went quiet, wanted to be alone..."
                                    value={event.behaviour || ''}
                                    onChange={(e) => {
                                      const events = [...(responses[`${item.id}_events`] || [{ day: '', event: '', behaviour: '', emotion: '', whatHelped: '', whatDidNotHelp: '' }])];
                                      events[idx] = { ...events[idx], behaviour: e.target.value };
                                      setResponses({...responses, [`${item.id}_events`]: events});
                                    }}
                                    rows="2"
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      fontSize: '0.9rem',
                                      resize: 'vertical',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                                
                                {/* Big feelings */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>What big feelings showed up?</label>
                                  <textarea
                                    placeholder="e.g., frustrated, overwhelmed, anxious, excited..."
                                    value={event.emotion || ''}
                                    onChange={(e) => {
                                      const events = [...(responses[`${item.id}_events`] || [{ day: '', event: '', behaviour: '', emotion: '', whatHelped: '', whatDidNotHelp: '' }])];
                                      events[idx] = { ...events[idx], emotion: e.target.value };
                                      setResponses({...responses, [`${item.id}_events`]: events});
                                    }}
                                    rows="2"
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      fontSize: '0.9rem',
                                      resize: 'vertical',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                                
                                {/* What Helped */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>What helped your child feel better?</label>
                                  <textarea
                                    placeholder="e.g., quiet space, favourite toy, a cuddle, going outside..."
                                    value={event.whatHelped || ''}
                                    onChange={(e) => {
                                      const events = [...(responses[`${item.id}_events`] || [{ day: '', event: '', behaviour: '', emotion: '', whatHelped: '', whatDidNotHelp: '' }])];
                                      events[idx] = { ...events[idx], whatHelped: e.target.value };
                                      setResponses({...responses, [`${item.id}_events`]: events});
                                    }}
                                    rows="2"
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      fontSize: '0.9rem',
                                      resize: 'vertical',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                                
                                {/* What didn't help this time */}
                                <div style={{ marginBottom: '4px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>What didn't help this time?</label>
                                  <textarea
                                    placeholder="e.g., talking too much, rushing, loud environment..."
                                    value={event.whatDidNotHelp || ''}
                                    onChange={(e) => {
                                      const events = [...(responses[`${item.id}_events`] || [{ day: '', event: '', behaviour: '', emotion: '', whatHelped: '', whatDidNotHelp: '' }])];
                                      events[idx] = { ...events[idx], whatDidNotHelp: e.target.value };
                                      setResponses({...responses, [`${item.id}_events`]: events});
                                    }}
                                    rows="2"
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      fontSize: '0.9rem',
                                      resize: 'vertical',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                          <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                            <button
                              type="button"
                              onClick={() => {
                                const events = [...(responses[`${item.id}_events`] || [{ day: '', event: '', behaviour: '', emotion: '', whatHelped: '', whatDidNotHelp: '' }])];
                                events.push({ day: '', event: '', behaviour: '', emotion: '', whatHelped: '', whatDidNotHelp: '' });
                                setResponses({...responses, [`${item.id}_events`]: events});
                              }}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500'
                              }}
                            >
                              + Add Another Entry
                            </button>
                          </div>
                        </div>
                      ) : item.input_type === "sleep_diary_table" ? (
                        <div className="sleep-diary-table" style={{ borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', backgroundColor: 'white' }}>
                          {/* Mobile-optimized card layout for Sleep Diary */}
                          <div className="sleep-entries">
                            {(responses[`${item.id}_sleep`] || [{ day: '', timeToBed: '', hoursOfSleep: '', childFeedback: '', childSleepScore: '', parentFeedback: '', parentIntervention: '' }]).map((entry, idx) => (
                              <div key={idx} style={{ 
                                padding: '16px', 
                                borderBottom: '1px solid #e2e8f0',
                                backgroundColor: idx % 2 === 0 ? 'white' : '#f8fafc'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                  <span style={{ fontWeight: '600', color: '#1e3a5f', fontSize: '0.9rem' }}>Night {idx + 1}</span>
                                  {(responses[`${item.id}_sleep`] || []).length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const entries = [...(responses[`${item.id}_sleep`] || [])];
                                        entries.splice(idx, 1);
                                        setResponses({...responses, [`${item.id}_sleep`]: entries});
                                      }}
                                      style={{
                                        padding: '4px 10px',
                                        backgroundColor: '#fee2e2',
                                        color: '#dc2626',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                                
                                {/* Day selector */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>Day/Night</label>
                                  <select
                                    value={entry.day || ''}
                                    onChange={(e) => {
                                      const entries = [...(responses[`${item.id}_sleep`] || [{ day: '', timeToBed: '', hoursOfSleep: '', childFeedback: '', childSleepScore: '', parentFeedback: '', parentIntervention: '' }])];
                                      entries[idx] = { ...entries[idx], day: e.target.value };
                                      setResponses({...responses, [`${item.id}_sleep`]: entries});
                                    }}
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      backgroundColor: 'white',
                                      fontSize: '0.9rem'
                                    }}
                                  >
                                    <option value="">Select day...</option>
                                    <option value="Monday">Monday</option>
                                    <option value="Tuesday">Tuesday</option>
                                    <option value="Wednesday">Wednesday</option>
                                    <option value="Thursday">Thursday</option>
                                    <option value="Friday">Friday</option>
                                    <option value="Saturday">Saturday</option>
                                    <option value="Sunday">Sunday</option>
                                  </select>
                                </div>
                                
                                {/* Time to bed and Hours of sleep - side by side on desktop */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                                  <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>Time to Bed</label>
                                    <input
                                      type="time"
                                      value={entry.timeToBed || ''}
                                      onChange={(e) => {
                                        const entries = [...(responses[`${item.id}_sleep`] || [{ day: '', timeToBed: '', hoursOfSleep: '', childFeedback: '', childSleepScore: '', parentFeedback: '', parentIntervention: '' }])];
                                        entries[idx] = { ...entries[idx], timeToBed: e.target.value };
                                        setResponses({...responses, [`${item.id}_sleep`]: entries});
                                      }}
                                      style={{ 
                                        width: '100%', 
                                        padding: '10px', 
                                        borderRadius: '6px', 
                                        border: '1px solid #cbd5e1',
                                        fontSize: '0.9rem',
                                        boxSizing: 'border-box'
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>Hours of Sleep</label>
                                    <input
                                      type="number"
                                      min="0"
                                      max="24"
                                      step="0.5"
                                      placeholder="e.g., 8.5"
                                      value={entry.hoursOfSleep || ''}
                                      onChange={(e) => {
                                        const entries = [...(responses[`${item.id}_sleep`] || [{ day: '', timeToBed: '', hoursOfSleep: '', childFeedback: '', childSleepScore: '', parentFeedback: '', parentIntervention: '' }])];
                                        entries[idx] = { ...entries[idx], hoursOfSleep: e.target.value };
                                        setResponses({...responses, [`${item.id}_sleep`]: entries});
                                      }}
                                      style={{ 
                                        width: '100%', 
                                        padding: '10px', 
                                        borderRadius: '6px', 
                                        border: '1px solid #cbd5e1',
                                        fontSize: '0.9rem',
                                        boxSizing: 'border-box'
                                      }}
                                    />
                                  </div>
                                </div>
                                
                                {/* Child Feedback */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>Child's Feedback</label>
                                  <textarea
                                    placeholder="e.g., took 3 tries to fall asleep, woke up twice during the night, had a bad dream..."
                                    value={entry.childFeedback || ''}
                                    onChange={(e) => {
                                      const entries = [...(responses[`${item.id}_sleep`] || [{ day: '', timeToBed: '', hoursOfSleep: '', childFeedback: '', childSleepScore: '', parentFeedback: '', parentIntervention: '' }])];
                                      entries[idx] = { ...entries[idx], childFeedback: e.target.value };
                                      setResponses({...responses, [`${item.id}_sleep`]: entries});
                                    }}
                                    rows="2"
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      fontSize: '0.9rem',
                                      resize: 'vertical',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                                
                                {/* Child Sleep Score */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>Child's Sleep Score (out of 10)</label>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                      type="range"
                                      min="1"
                                      max="10"
                                      value={entry.childSleepScore || 5}
                                      onChange={(e) => {
                                        const entries = [...(responses[`${item.id}_sleep`] || [{ day: '', timeToBed: '', hoursOfSleep: '', childFeedback: '', childSleepScore: '', parentFeedback: '', parentIntervention: '' }])];
                                        entries[idx] = { ...entries[idx], childSleepScore: e.target.value };
                                        setResponses({...responses, [`${item.id}_sleep`]: entries});
                                      }}
                                      style={{ flex: 1 }}
                                    />
                                    <span style={{ 
                                      minWidth: '40px', 
                                      textAlign: 'center', 
                                      fontWeight: '600', 
                                      color: '#2563eb',
                                      fontSize: '1.1rem'
                                    }}>
                                      {entry.childSleepScore || '-'}
                                    </span>
                                  </div>
                                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                                    How well did your child feel they slept? (1 = very poorly, 10 = excellent)
                                  </p>
                                </div>
                                
                                {/* Parent Feedback */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>Parent's Observations</label>
                                  <textarea
                                    placeholder="e.g., seemed restless, slept through the night, called out once..."
                                    value={entry.parentFeedback || ''}
                                    onChange={(e) => {
                                      const entries = [...(responses[`${item.id}_sleep`] || [{ day: '', timeToBed: '', hoursOfSleep: '', childFeedback: '', childSleepScore: '', parentFeedback: '', parentIntervention: '' }])];
                                      entries[idx] = { ...entries[idx], parentFeedback: e.target.value };
                                      setResponses({...responses, [`${item.id}_sleep`]: entries});
                                    }}
                                    rows="2"
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      fontSize: '0.9rem',
                                      resize: 'vertical',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                                
                                {/* Parenting Intervention */}
                                <div style={{ marginBottom: '4px' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>Parenting Interventions</label>
                                  <textarea
                                    placeholder="e.g., read a story, used white noise, let them sleep in our room, gave a warm drink..."
                                    value={entry.parentIntervention || ''}
                                    onChange={(e) => {
                                      const entries = [...(responses[`${item.id}_sleep`] || [{ day: '', timeToBed: '', hoursOfSleep: '', childFeedback: '', childSleepScore: '', parentFeedback: '', parentIntervention: '' }])];
                                      entries[idx] = { ...entries[idx], parentIntervention: e.target.value };
                                      setResponses({...responses, [`${item.id}_sleep`]: entries});
                                    }}
                                    rows="2"
                                    style={{ 
                                      width: '100%', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #cbd5e1',
                                      fontSize: '0.9rem',
                                      resize: 'vertical',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                          <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                            <button
                              type="button"
                              onClick={() => {
                                const entries = [...(responses[`${item.id}_sleep`] || [{ day: '', timeToBed: '', hoursOfSleep: '', childFeedback: '', childSleepScore: '', parentFeedback: '', parentIntervention: '' }])];
                                entries.push({ day: '', timeToBed: '', hoursOfSleep: '', childFeedback: '', childSleepScore: '', parentFeedback: '', parentIntervention: '' });
                                setResponses({...responses, [`${item.id}_sleep`]: entries});
                              }}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500'
                              }}
                            >
                              + Add Another Night
                            </button>
                          </div>
                        </div>
                      ) : item.input_type === "discovery_item" ? (
                        <div className="discovery-item" style={{ 
                          backgroundColor: '#fefefe',
                          borderRadius: '12px',
                          padding: '20px',
                          marginBottom: '8px',
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                          {/* Question description */}
                          <p style={{ 
                            fontSize: '0.95rem', 
                            color: '#374151', 
                            lineHeight: '1.7',
                            marginBottom: '16px'
                          }}>
                            {item.description}
                          </p>
                          
                          {/* Rating selection */}
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '8px' }}>
                              How much does this describe you/your child?
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {[
                                { value: 'not_at_all', label: 'Not at all' },
                                { value: 'a_little', label: 'A little' },
                                { value: 'somewhat', label: 'Somewhat' },
                                { value: 'a_lot', label: 'A lot' },
                                { value: 'exactly', label: 'Exactly like us' }
                              ].map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => setResponses({...responses, [`${item.id}_rating`]: option.value})}
                                  style={{
                                    padding: '8px 14px',
                                    borderRadius: '20px',
                                    border: responses[`${item.id}_rating`] === option.value ? '2px solid #2563eb' : '1px solid #d1d5db',
                                    backgroundColor: responses[`${item.id}_rating`] === option.value ? '#eff6ff' : 'white',
                                    color: responses[`${item.id}_rating`] === option.value ? '#1d4ed8' : '#4b5563',
                                    fontWeight: responses[`${item.id}_rating`] === option.value ? '600' : '400',
                                    fontSize: '0.85rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                  }}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Optional notes */}
                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>
                              Examples are really helpful for us to build a full clinical picture
                            </label>
                            <textarea
                              placeholder="Share any examples or thoughts..."
                              value={responses[`${item.id}_notes`] || ''}
                              onChange={(e) => setResponses({...responses, [`${item.id}_notes`]: e.target.value})}
                              rows="2"
                              style={{ 
                                width: '100%', 
                                padding: '10px', 
                                borderRadius: '8px', 
                                border: '1px solid #d1d5db',
                                fontSize: '0.9rem',
                                resize: 'vertical',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>
                        </div>
                      ) : item.input_type === "ace_symptom_dual" ? (
                        <div className="ace-symptom-dual">
                          {/* HOME Section */}
                          <div style={{ 
                            marginBottom: '24px',
                            padding: '16px',
                            backgroundColor: '#f0f9ff',
                            borderRadius: '8px',
                            border: '1px solid rgba(74, 144, 181, 0.2)'
                          }}>
                            <div style={{ 
                              fontSize: '1rem', 
                              fontWeight: '700', 
                              color: '#2563eb', 
                              marginBottom: '12px' 
                            }}>
                              HOME
                            </div>
                            <p style={{ 
                              fontSize: '0.9375rem', 
                              color: '#1a202c', 
                              marginBottom: '12px',
                              lineHeight: '1.6'
                            }}>
                              {item.home_description}
                            </p>
                            <p style={{ 
                              fontSize: '0.875rem', 
                              color: '#4A90B5', 
                              fontStyle: 'italic',
                              marginBottom: '16px'
                            }}>
                              {item.home_prompt}
                            </p>
                            {/* HOME Yes/No */}
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1a202c', marginBottom: '8px' }}>
                                Is this symptom present at HOME?
                              </div>
                              <div style={{ display: 'flex', gap: '16px' }}>
                                {[{value: 'yes', label: 'Yes'}, {value: 'no', label: 'No'}].map((option) => (
                                  <div 
                                    key={option.value}
                                    className={`radio-option clickable ${responses[`${item.id}_home_present`] === option.value ? 'selected' : ''}`}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '8px 16px',
                                      borderRadius: '8px',
                                      border: responses[`${item.id}_home_present`] === option.value ? '2px solid #2563eb' : '1px solid rgba(74, 144, 181, 0.3)',
                                      backgroundColor: responses[`${item.id}_home_present`] === option.value ? 'rgba(37, 99, 235, 0.1)' : 'white',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => {
                                      setResponses({...responses, [`${item.id}_home_present`]: option.value});
                                      setIncompleteItems(prev => prev.filter(id => id !== item.id));
                                    }}
                                    data-testid={`response-home-${item.id}-${option.value}`}
                                  >
                                    <input
                                      type="radio"
                                      checked={responses[`${item.id}_home_present`] === option.value}
                                      onChange={() => {}}
                                      style={{ marginRight: '8px', accentColor: '#2563eb' }}
                                    />
                                    <span>{option.label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            {/* HOME Notes */}
                            <textarea
                              placeholder="Enter observations and examples for HOME..."
                              value={responses[`${item.id}_home_notes`] || ""}
                              onChange={(e) => setResponses({...responses, [`${item.id}_home_notes`]: e.target.value})}
                              rows="3"
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid rgba(74, 144, 181, 0.2)',
                                fontSize: '0.875rem',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                minHeight: '80px',
                                backgroundColor: 'white'
                              }}
                              data-testid={`response-home-notes-${item.id}`}
                            />
                          </div>

                          {/* SCHOOL Section */}
                          <div style={{ 
                            marginBottom: '24px',
                            padding: '16px',
                            backgroundColor: '#fef3c7',
                            borderRadius: '8px',
                            border: '1px solid rgba(217, 119, 6, 0.2)'
                          }}>
                            <div style={{ 
                              fontSize: '1rem', 
                              fontWeight: '700', 
                              color: '#d97706', 
                              marginBottom: '12px' 
                            }}>
                              SCHOOL
                            </div>
                            <p style={{ 
                              fontSize: '0.9375rem', 
                              color: '#1a202c', 
                              marginBottom: '12px',
                              lineHeight: '1.6'
                            }}>
                              {item.school_description}
                            </p>
                            <p style={{ 
                              fontSize: '0.875rem', 
                              color: '#d97706', 
                              fontStyle: 'italic',
                              marginBottom: '16px'
                            }}>
                              {item.school_prompt}
                            </p>
                            {/* SCHOOL Yes/No */}
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1a202c', marginBottom: '8px' }}>
                                Is this symptom present at SCHOOL?
                              </div>
                              <div style={{ display: 'flex', gap: '16px' }}>
                                {[{value: 'yes', label: 'Yes'}, {value: 'no', label: 'No'}].map((option) => (
                                  <div 
                                    key={option.value}
                                    className={`radio-option clickable ${responses[`${item.id}_school_present`] === option.value ? 'selected' : ''}`}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '8px 16px',
                                      borderRadius: '8px',
                                      border: responses[`${item.id}_school_present`] === option.value ? '2px solid #d97706' : '1px solid rgba(217, 119, 6, 0.3)',
                                      backgroundColor: responses[`${item.id}_school_present`] === option.value ? 'rgba(217, 119, 6, 0.1)' : 'white',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => {
                                      setResponses({...responses, [`${item.id}_school_present`]: option.value});
                                      setIncompleteItems(prev => prev.filter(id => id !== item.id));
                                    }}
                                    data-testid={`response-school-${item.id}-${option.value}`}
                                  >
                                    <input
                                      type="radio"
                                      checked={responses[`${item.id}_school_present`] === option.value}
                                      onChange={() => {}}
                                      style={{ marginRight: '8px', accentColor: '#d97706' }}
                                    />
                                    <span>{option.label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            {/* SCHOOL Notes */}
                            <textarea
                              placeholder="Enter observations and examples for SCHOOL..."
                              value={responses[`${item.id}_school_notes`] || ""}
                              onChange={(e) => setResponses({...responses, [`${item.id}_school_notes`]: e.target.value})}
                              rows="3"
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid rgba(217, 119, 6, 0.2)',
                                fontSize: '0.875rem',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                minHeight: '80px',
                                backgroundColor: 'white'
                              }}
                              data-testid={`response-school-notes-${item.id}`}
                            />
                          </div>

                          {/* Symptom Criterion Met */}
                          <div style={{ 
                            padding: '16px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px',
                            border: '2px solid #4A90B5'
                          }}>
                            <div style={{ 
                              fontSize: '0.9375rem', 
                              fontWeight: '700', 
                              color: '#1a202c', 
                              marginBottom: '12px' 
                            }}>
                              Symptom criterion met:
                            </div>
                            <div style={{ display: 'flex', gap: '16px' }}>
                              {[{value: 'yes', label: 'Yes'}, {value: 'no', label: 'No'}].map((option) => (
                                <div 
                                  key={option.value}
                                  className={`radio-option clickable ${responses[`${item.id}_criterion_met`] === option.value ? 'selected' : ''}`}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    border: responses[`${item.id}_criterion_met`] === option.value ? '2px solid #4A90B5' : '1px solid rgba(74, 144, 181, 0.3)',
                                    backgroundColor: responses[`${item.id}_criterion_met`] === option.value ? 'rgba(74, 144, 181, 0.15)' : 'white',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                  }}
                                  onClick={() => {
                                    setResponses({...responses, [`${item.id}_criterion_met`]: option.value});
                                    setIncompleteItems(prev => prev.filter(id => id !== item.id));
                                  }}
                                  data-testid={`response-criterion-${item.id}-${option.value}`}
                                >
                                  <input
                                    type="radio"
                                    checked={responses[`${item.id}_criterion_met`] === option.value}
                                    onChange={() => {}}
                                    style={{ marginRight: '8px', accentColor: '#4A90B5', width: '18px', height: '18px' }}
                                  />
                                  <span>{option.label}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <RadioGroup
                            value={responses[item.id]?.toString() || ""}
                            onValueChange={(value) => {
                              // Check if value should be parsed as number or kept as string
                              const numVal = parseInt(value);
                              const finalValue = isNaN(numVal) ? value : numVal;
                              setResponses({...responses, [item.id]: finalValue});
                              setIncompleteItems(prev => prev.filter(id => id !== item.id));
                              setCheckAnswerIds(prev => prev.filter(id => id !== String(item.id)));
                            }}
                            className="radio-group"
                          >
                            {(item.options || assessmentConfig.response_format).map((option) => (
                              <div 
                                key={option.value} 
                                className={`radio-option clickable ${responses[item.id]?.toString() === option.value.toString() ? 'selected' : ''}`}
                                data-testid={`response-option-${item.id}-${option.value}`}
                                onClick={() => {
                                  const numVal = parseInt(option.value);
                                  const finalValue = isNaN(numVal) ? option.value : numVal;
                                  setResponses({...responses, [item.id]: finalValue});
                                  setIncompleteItems(prev => prev.filter(id => id !== item.id));
                              setCheckAnswerIds(prev => prev.filter(id => id !== String(item.id)));
                                }}
                              >
                                <RadioGroupItem
                                  value={option.value.toString()}
                                  id={`q${item.id}-${option.value}`}
                                  className="radio-input"
                                />
                                <Label
                                  htmlFor={`q${item.id}-${option.value}`}
                                  className="radio-label"
                                >
                                  {option.label || option.text}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                          
                          {/* Conditional follow-up text field for ACE-SR-12 */}
                          {item.conditional_followup && 
                           item.conditional_followup.trigger_values?.includes(responses[item.id]) && (
                            <div className="followup-section" style={{ marginTop: '16px' }}>
                              {item.conditional_followup.questions.map((followupQ) => (
                                <div key={followupQ.id} style={{ marginBottom: '12px' }}>
                                  <Label 
                                    htmlFor={`followup-${followupQ.id}`}
                                    style={{ 
                                      display: 'block', 
                                      marginBottom: '8px',
                                      fontSize: '0.9rem',
                                      color: '#4A90B5',
                                      fontWeight: '500'
                                    }}
                                  >
                                    {followupQ.text}
                                  </Label>
                                  <textarea
                                    id={`followup-${followupQ.id}`}
                                    placeholder="Please provide your response..."
                                    value={responses[followupQ.id] || ""}
                                    onChange={(e) => setResponses({...responses, [followupQ.id]: e.target.value})}
                                    rows="4"
                                    style={{
                                      width: '100%',
                                      padding: '12px',
                                      borderRadius: '8px',
                                      border: '1px solid rgba(74, 144, 181, 0.3)',
                                      fontSize: '0.9375rem',
                                      fontFamily: 'inherit',
                                      resize: 'vertical',
                                      minHeight: '100px',
                                      backgroundColor: '#f8fafc',
                                      color: '#1a202c'
                                    }}
                                    data-testid={`followup-input-${followupQ.id}`}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                  )
                )})}
              </div>

              {(assessmentConfig.is_intake_form || selectedAssessment === 'File-Upload') ? (
              <div className="assessment-footer assessment-footer--quiet">
                <button
                  type="button"
                  onClick={() => setShowLeaveConfirm(true)}
                  className="leave-link"
                  data-testid="cancel-button"
                >
                  Leave this form
                </button>
              </div>
              ) : (
              <div className="assessment-footer">
                <Button
                  variant="outline"
                  onClick={() => setShowLeaveConfirm(true)}
                  className="cancel-button"
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                {showNamesPage ? (
                <Button
                  onClick={() => setShowNamesPage(false)}
                  disabled={!name.trim() || !completedByName.trim()}
                  className="submit-button"
                  data-testid="names-next-button"
                >
                  Next
                </Button>
                ) : (selectedAssessment !== 'File-Upload' && !assessmentConfig.is_intake_form && (
                <Button
                  onClick={submitAssessment}
                  disabled={loading || !SENDING_ON}
                  className="submit-button"
                  data-testid="submit-assessment-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="button-icon spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="button-icon" />
                      Submit Assessment
                    </>
                  )}
                </Button>
                ))}
                {!assessmentConfig.is_intake_form && selectedAssessment !== 'File-Upload' && <PreviewNote inline />}
              </div>
              )}
            </>
          ) : (
            <div className="loading-skeleton">
              <div className="skeleton-header">
                <div className="skeleton-title"></div>
                <div className="skeleton-instructions"></div>
              </div>
              <div className="skeleton-questions">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="skeleton-question">
                    <div className="skeleton-question-text"></div>
                    <div className="skeleton-options">
                      {[...Array(4)].map((_, j) => (
                        <div key={j} className="skeleton-option"></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {page === "results" && results && (
        <ResultsPage name={name} date={date} results={results} assessmentDisplayName={assessmentConfig?.name || results?.assessment_type} />
      )}
    </div>
  );
}

export default App;
